/**
 * StudoX Core — The Ecosystem Hub
 * ─────────────────────────────────────────────────────────────────────────────
 * Triple-A entry animation + app launcher + SSO token bridge.
 * Accessible without login. Writes SSO token to localStorage so other
 * StudoX apps can pick it up and auto-login.
 */
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "@/store/authStore";
import { readInviteCode, clearInviteFromURL } from "@/lib/studox-auth/invite.js";
import CoreDashboard from "./CoreDashboard";
import ThreeBackground from "./NexusSingularity";
import {
  MeetMark, QuestMark, MusicMark, PhonkMark, MicMark, LyricsMark, DiscordMark,
  InviteMark, EventsMark, HelpMark, SupportMark, StatusMark, InvestMark, AdultingMark,
  HubMark, InfoMark, ImpactMark,
  FlowMark as FlowLogo, StepMark as StepLogo, ClubsMark as ClubsLogo,
  CreateMark as CreateLogo, CinemaMark as CinemaLogo,
  LooksMark, DossierMark, HorrorMark,
  StartupMark, FutureCityMark, RapVidMark,
} from "./CoreLogos";
import CoreBridge from "./CoreBridge";
import CookieConsent from "./CookieConsent";
import NovaBuddy from "@/components/buddy/NovaBuddy";

const BACKEND_URL = import.meta.env.VITE_API_URL?.replace("/api", "") || "http://localhost:4000";

/* ── SSO: write token so other StudoX apps can read it ────────────────────── */
const SSO_KEY = "studox-core-sso-v1";
export function writeSSOToken(token) {
  try { localStorage.setItem(SSO_KEY, JSON.stringify({ token, ts: Date.now() })); } catch {}
}
export function readSSOToken() {
  try {
    const raw = localStorage.getItem(SSO_KEY);
    if (!raw) return null;
    const { token, ts } = JSON.parse(raw);
    if (Date.now() - ts > 24 * 60 * 60 * 1000) return null; // 24h TTL
    return token;
  } catch { return null; }
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* LOGO MARKS                                                                  */
/* ═══════════════════════════════════════════════════════════════════════════ */

/* StudoX Flow — Isometric premium cube */
function FlowMark({ size = 80, glow = false, animated = false }) {
  const s = size / 80;
  const cx = 40 * s, cy = 40 * s;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ overflow: "visible" }}>
      <defs>
        <linearGradient id="flow-top" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#e9d5ff" />
          <stop offset="100%" stopColor="#a78bfa" />
        </linearGradient>
        <linearGradient id="flow-left" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#4c1d95" />
          <stop offset="100%" stopColor="#6d28d9" />
        </linearGradient>
        <linearGradient id="flow-right" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#0891b2" />
          <stop offset="100%" stopColor="#06b6d4" />
        </linearGradient>
        {glow && (
          <filter id="flow-glow" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation={3 * s} result="blur"/>
            <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
        )}
      </defs>
      <g filter={glow ? "url(#flow-glow)" : undefined}
         style={animated ? { animation: "float 3.5s ease-in-out infinite" } : {}}>
        {/* Top face */}
        <polygon points={`${cx},${12*s} ${cx+33*s},${28*s} ${cx},${44*s} ${cx-33*s},${28*s}`} fill="url(#flow-top)" />
        {/* Left face */}
        <polygon points={`${cx-33*s},${28*s} ${cx},${44*s} ${cx},${68*s} ${cx-33*s},${52*s}`} fill="url(#flow-left)" />
        {/* Right face */}
        <polygon points={`${cx},${44*s} ${cx+33*s},${28*s} ${cx+33*s},${52*s} ${cx},${68*s}`} fill="url(#flow-right)" />
        {/* Top edge highlight */}
        <polygon points={`${cx},${12*s} ${cx+33*s},${28*s} ${cx},${44*s} ${cx-33*s},${28*s}`} fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth={s}/>
      </g>
    </svg>
  );
}

/* StudoX Step — 3-D isometric staircase, warm playful palette */
function StepMark({ size = 80, glow = false, animated = false, dimmed = false }) {
  const s = size / 80;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ overflow: "visible", opacity: dimmed ? 0.45 : 1 }}>
      <defs>
        <linearGradient id="step-t1" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fef08a" /><stop offset="100%" stopColor="#fbbf24" />
        </linearGradient>
        <linearGradient id="step-t2" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fed7aa" /><stop offset="100%" stopColor="#f97316" />
        </linearGradient>
        <linearGradient id="step-t3" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fca5a5" /><stop offset="100%" stopColor="#ef4444" />
        </linearGradient>
        <linearGradient id="step-s1" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#d97706" /><stop offset="100%" stopColor="#92400e" />
        </linearGradient>
        <linearGradient id="step-s2" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#c2410c" /><stop offset="100%" stopColor="#7c2d12" />
        </linearGradient>
        <linearGradient id="step-s3" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#b91c1c" /><stop offset="100%" stopColor="#7f1d1d" />
        </linearGradient>
        {glow && (
          <filter id="step-glow" x="-40%" y="-40%" width="180%" height="180%">
            <feGaussianBlur stdDeviation={4 * s} result="blur"/>
            <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
        )}
      </defs>
      <g filter={glow ? "url(#step-glow)" : undefined}
         style={animated ? { animation: "float 3.5s ease-in-out 0.5s infinite" } : {}}>
        {/* Step 1 (bottom, yellow) — top + front + side */}
        {/* top face */}
        <polygon points={`${11*s},${54*s} ${38*s},${40*s} ${38*s},${54*s} ${11*s},${68*s}`} fill="url(#step-t1)" />
        {/* front face */}
        <polygon points={`${11*s},${54*s} ${38*s},${54*s} ${38*s},${68*s} ${11*s},${68*s}`} fill="url(#step-s1)" />
        {/* Step 2 (middle, orange) */}
        <polygon points={`${25*s},${40*s} ${52*s},${26*s} ${52*s},${40*s} ${25*s},${54*s}`} fill="url(#step-t2)" />
        <polygon points={`${25*s},${40*s} ${52*s},${40*s} ${52*s},${54*s} ${25*s},${54*s}`} fill="url(#step-s2)" />
        {/* side connector step 2 */}
        <polygon points={`${38*s},${54*s} ${52*s},${40*s} ${52*s},${54*s} ${38*s},${54*s}`} fill="rgba(0,0,0,0.15)" />
        {/* Step 3 (top, coral/red) */}
        <polygon points={`${39*s},${26*s} ${66*s},${12*s} ${66*s},${26*s} ${39*s},${40*s}`} fill="url(#step-t3)" />
        <polygon points={`${39*s},${26*s} ${66*s},${26*s} ${66*s},${40*s} ${39*s},${40*s}`} fill="url(#step-s3)" />
        {/* side connector step 3 */}
        <polygon points={`${52*s},${40*s} ${66*s},${26*s} ${66*s},${40*s} ${52*s},${40*s}`} fill="rgba(0,0,0,0.15)" />
        {/* Highlight edges */}
        <line x1={`${11*s}`} y1={`${54*s}`} x2={`${38*s}`} y2={`${40*s}`} stroke="rgba(255,255,255,0.4)" strokeWidth={s} />
        <line x1={`${25*s}`} y1={`${40*s}`} x2={`${52*s}`} y2={`${26*s}`} stroke="rgba(255,255,255,0.4)" strokeWidth={s} />
        <line x1={`${39*s}`} y1={`${26*s}`} x2={`${66*s}`} y2={`${12*s}`} stroke="rgba(255,255,255,0.4)" strokeWidth={s} />
      </g>
    </svg>
  );
}

function ClubsMark({ size = 80, glow = false, animated = false }) {
  const s = size / 80;
  return (
    <svg width={size} height={size} viewBox="0 0 80 80" style={{ overflow: "visible" }}>
      <defs>
        <radialGradient id="clubs-p1" cx="38%" cy="28%" r="65%">
          <stop offset="0%" stopColor="#86efac"/>
          <stop offset="100%" stopColor="#15803d"/>
        </radialGradient>
        <radialGradient id="clubs-p2" cx="38%" cy="28%" r="65%">
          <stop offset="0%" stopColor="#6ee7b7"/>
          <stop offset="100%" stopColor="#065f46"/>
        </radialGradient>
        <radialGradient id="clubs-p3" cx="38%" cy="28%" r="65%">
          <stop offset="0%" stopColor="#a7f3d0"/>
          <stop offset="100%" stopColor="#047857"/>
        </radialGradient>
        {glow && (
          <filter id="clubs-glow" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation={3 * s} result="blur"/>
            <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
        )}
      </defs>
      <g filter={glow ? "url(#clubs-glow)" : undefined}
         style={animated ? { animation: "float 3.5s ease-in-out 0.25s infinite" } : {}}>

        {/* Unity arc at base connecting all three */}
        <path d="M14 64 Q40 72 66 64" fill="none" stroke="rgba(110,231,183,0.45)" strokeWidth={2.5*s} strokeLinecap="round"/>

        {/* Left person — body */}
        <rect x={13*s} y={39*s} width={13*s} height={18*s} rx={6.5*s} fill="url(#clubs-p2)" opacity="0.95"/>
        {/* Left person — head */}
        <circle cx={19.5*s} cy={31*s} r={8*s} fill="url(#clubs-p2)"/>
        <ellipse cx={17.5*s} cy={28*s} rx={3.5*s} ry={2*s} fill="white" opacity="0.3" transform={`rotate(-18 ${17.5*s} ${28*s})`}/>

        {/* Right person — body */}
        <rect x={54*s} y={39*s} width={13*s} height={18*s} rx={6.5*s} fill="url(#clubs-p3)" opacity="0.95"/>
        {/* Right person — head */}
        <circle cx={60.5*s} cy={31*s} r={8*s} fill="url(#clubs-p3)"/>
        <ellipse cx={58.5*s} cy={28*s} rx={3.5*s} ry={2*s} fill="white" opacity="0.28" transform={`rotate(-18 ${58.5*s} ${28*s})`}/>

        {/* Center person (taller/larger = leader) — body */}
        <rect x={33*s} y={35*s} width={14*s} height={21*s} rx={7*s} fill="url(#clubs-p1)"/>
        {/* Center person — head */}
        <circle cx={40*s} cy={25*s} r={9.5*s} fill="url(#clubs-p1)"/>
        <ellipse cx={37.5*s} cy={21.5*s} rx={4*s} ry={2.5*s} fill="white" opacity="0.38" transform={`rotate(-18 ${37.5*s} ${21.5*s})`}/>

        {/* Accent dots (connection sparkles) */}
        <circle cx={40*s} cy={10*s} r={2.2*s} fill="#4ade80" opacity="0.85"/>
        <circle cx={8*s}  cy={52*s} r={1.8*s} fill="#34d399" opacity="0.7"/>
        <circle cx={72*s} cy={52*s} r={1.8*s} fill="#6ee7b7" opacity="0.7"/>
      </g>
    </svg>
  );
}

/* StudoX Core — "Nexus Gateway": crystalline core inside a portal ring,
   3 ecosystem satellites feeding energy in. Mirrors the 3D background's
   visual identity (portal rings + hub nodes + violet/cyan/gold energy). */
function CoreMark({ size = 80, glow = false, animated = false }) {
  const s = size / 80;
  return (
    <svg width={size} height={size} viewBox="0 0 80 80" style={{ overflow: "visible" }}>
      <defs>
        <radialGradient id="cm-orb" cx="38%" cy="28%" r="70%">
          <stop offset="0%"  stopColor="#fffdf5"/>
          <stop offset="30%" stopColor="#fde68a"/>
          <stop offset="62%" stopColor="#a855f7"/>
          <stop offset="100%" stopColor="#2e1065"/>
        </radialGradient>
        <radialGradient id="cm-na" cx="35%" cy="25%" r="70%">
          <stop offset="0%" stopColor="#ecfeff"/>
          <stop offset="55%" stopColor="#22d3ee"/>
          <stop offset="100%" stopColor="#0e7490"/>
        </radialGradient>
        <radialGradient id="cm-nb" cx="35%" cy="25%" r="70%">
          <stop offset="0%" stopColor="#f5f3ff"/>
          <stop offset="55%" stopColor="#a78bfa"/>
          <stop offset="100%" stopColor="#5b21b6"/>
        </radialGradient>
        <radialGradient id="cm-nc" cx="35%" cy="25%" r="70%">
          <stop offset="0%" stopColor="#fffbeb"/>
          <stop offset="55%" stopColor="#fbbf24"/>
          <stop offset="100%" stopColor="#92400e"/>
        </radialGradient>
        {/* Portal ring — thick gateway band, gold→violet→cyan energy gradient */}
        <linearGradient id="cm-portal" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%"   stopColor="#fbbf24"/>
          <stop offset="35%"  stopColor="#c084fc"/>
          <stop offset="68%"  stopColor="#8b5cf6"/>
          <stop offset="100%" stopColor="#22d3ee"/>
        </linearGradient>
        <linearGradient id="cm-r2" x1="0%" y1="50%" x2="100%" y2="50%">
          <stop offset="0%"   stopColor="#06b6d4" stopOpacity="0.12"/>
          <stop offset="50%"  stopColor="#8b5cf6" stopOpacity="0.75"/>
          <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0.08"/>
        </linearGradient>
        {glow && (
          <filter id="cm-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation={3.5 * s} result="blur"/>
            <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
        )}
      </defs>
      <g filter={glow ? "url(#cm-glow)" : undefined}
         style={animated ? { animation: "float 3.5s ease-in-out infinite" } : {}}>

        {/* Faint outer containment circle */}
        <circle cx="40" cy="40" r="38.5" fill="none" stroke="rgba(139,92,246,0.14)" strokeWidth={0.7*s}/>

        {/* ── The Portal — a real gateway ring, not a thin outline ── */}
        <circle cx="40" cy="40" r="30" fill="none" stroke="url(#cm-portal)" strokeWidth={3.4*s} opacity="0.92"/>
        <circle cx="40" cy="40" r="30" fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth={0.7*s}/>
        {/* Tick marks around the portal band — HUD/gateway detailing */}
        {Array.from({ length: 16 }).map((_, i) => {
          const a = (i / 16) * Math.PI * 2;
          const r1 = 27.2, r2 = 32.8;
          return (
            <line key={i}
              x1={40 + r1*Math.cos(a)} y1={40 + r1*Math.sin(a)}
              x2={40 + r2*Math.cos(a)} y2={40 + r2*Math.sin(a)}
              stroke="rgba(255,255,255,0.22)" strokeWidth={0.6*s}
            />
          );
        })}

        {/* ── LIVING PORTAL — self-contained SMIL motion, runs everywhere ──
            A bright energy dash sweeps the gateway, a spark orbits it, and a
            faint counter-rotating HUD ring breathes outside. Every CoreMark
            instance (login modal, bridge header, boot logo) now reads as a
            powered machine instead of a static badge. */}
        <circle cx="40" cy="40" r="30" fill="none" stroke="#ffffff" strokeWidth={1.1*s} strokeLinecap="round"
          strokeDasharray={`${14*s} ${175*s}`} opacity="0.85">
          <animateTransform attributeName="transform" type="rotate" from="0 40 40" to="360 40 40" dur="7s" repeatCount="indefinite"/>
        </circle>
        <circle cx="40" cy="40" r="35" fill="none" stroke="#22d3ee" strokeWidth={0.7*s}
          strokeDasharray={`${3*s} ${9*s}`} opacity="0.4">
          <animateTransform attributeName="transform" type="rotate" from="360 40 40" to="0 40 40" dur="18s" repeatCount="indefinite"/>
        </circle>
        <g>
          <animateTransform attributeName="transform" type="rotate" from="0 40 40" to="360 40 40" dur="5.5s" repeatCount="indefinite"/>
          <circle cx="70" cy="40" r={4.8*s} fill="#22d3ee" opacity="0.3"/>
          <circle cx="70" cy="40" r={2.4*s} fill="#ffffff"/>
        </g>

        {/* Second inner energy ellipse — now slowly counter-rotating for depth */}
        <g>
          <animateTransform attributeName="transform" type="rotate" from="360 40 40" to="0 40 40" dur="24s" repeatCount="indefinite"/>
          <ellipse cx="40" cy="40" rx="22" ry="9" fill="none" stroke="url(#cm-r2)" strokeWidth={1*s} opacity="0.6"
            transform="rotate(-32 40 40)"/>
        </g>

        {/* Dashed connectors: satellites → gate edge — dashes now FLOW inward,
            so the energy feed reads as live current, not a static dotted line */}
        <line x1="40"   y1="9.5"  x2="40"   y2="22.5"
          stroke="rgba(34,211,238,0.6)"  strokeWidth={1.2*s} strokeDasharray={`${2.5*s} ${2*s}`}>
          <animate attributeName="stroke-dashoffset" from={4.5*s} to={0} dur="0.7s" repeatCount="indefinite"/>
        </line>
        <line x1="66"   y1="60.5" x2="55.5" y2="52.2"
          stroke="rgba(139,92,246,0.6)"  strokeWidth={1.2*s} strokeDasharray={`${2.5*s} ${2*s}`}>
          <animate attributeName="stroke-dashoffset" from={4.5*s} to={0} dur="0.85s" repeatCount="indefinite"/>
        </line>
        <line x1="14"   y1="60.5" x2="24.5" y2="52.2"
          stroke="rgba(251,191,36,0.6)"  strokeWidth={1.2*s} strokeDasharray={`${2.5*s} ${2*s}`}>
          <animate attributeName="stroke-dashoffset" from={4.5*s} to={0} dur="1s" repeatCount="indefinite"/>
        </line>

        {/* Satellite A — top — cyan */}
        <circle cx="40" cy="8" r={5.6*s} fill="url(#cm-na)"/>
        <circle cx="40" cy="8" r={5.6*s} fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth={0.5*s}/>
        <ellipse cx="38.6" cy="6.3" rx={2.6*s} ry={1.5*s} fill="white" opacity="0.55" transform="rotate(-15 38.6 6.3)"/>

        {/* Satellite B — bottom-right — violet */}
        <circle cx="67" cy="61.5" r={4.9*s} fill="url(#cm-nb)"/>
        <circle cx="67" cy="61.5" r={4.9*s} fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth={0.5*s}/>
        <ellipse cx="65.6" cy="59.9" rx={2.2*s} ry={1.3*s} fill="white" opacity="0.5" transform="rotate(-15 65.6 59.9)"/>

        {/* Satellite C — bottom-left — gold */}
        <circle cx="13" cy="61.5" r={4.5*s} fill="url(#cm-nc)"/>
        <circle cx="13" cy="61.5" r={4.5*s} fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth={0.5*s}/>
        <ellipse cx="11.7" cy="59.9" rx={2*s} ry={1.2*s} fill="white" opacity="0.45" transform="rotate(-15 11.7 59.9)"/>

        {/* ── Crystalline nucleus — faceted core, not a flat orb ── */}
        <polygon points="40,27.5 49,33.5 49,46.5 40,52.5 31,46.5 31,33.5"
          fill="url(#cm-orb)" stroke="rgba(255,255,255,0.28)" strokeWidth={0.6*s}/>
        <polygon points="40,27.5 49,33.5 40,40 31,33.5" fill="white" opacity="0.16"/>
        <line x1="40" y1="27.5" x2="40" y2="52.5" stroke="rgba(255,255,255,0.18)" strokeWidth={0.5*s}/>
        <line x1="31" y1="33.5" x2="49" y2="46.5" stroke="rgba(255,255,255,0.10)" strokeWidth={0.5*s}/>
        <line x1="49" y1="33.5" x2="31" y2="46.5" stroke="rgba(255,255,255,0.10)" strokeWidth={0.5*s}/>
        <circle cx="40" cy="40" r={4.2*s} fill="rgba(255,255,255,0.85)">
          <animate attributeName="opacity" values="0.65;1;0.65" dur="2.2s" repeatCount="indefinite"/>
        </circle>
        <circle cx="40" cy="40" r={1.8*s} fill="white"/>
        {/* Lens-flare sparkle cross — slowly rotating like a real glint */}
        <g opacity="0.8">
          <animateTransform attributeName="transform" type="rotate" from="0 40 40" to="360 40 40" dur="16s" repeatCount="indefinite"/>
          <rect x={40-9*s} y={40-0.45*s} width={18*s} height={0.9*s} fill="white" opacity="0.55"/>
          <rect x={40-0.45*s} y={40-9*s} width={0.9*s} height={18*s} fill="white" opacity="0.55"/>
        </g>
      </g>
    </svg>
  );
}

/* StudoX Create — Isometric design canvas + pen nib + rainbow stroke */
function CreateMark({ size = 80, glow = false, animated = false }) {
  const s = size / 80;
  return (
    <svg width={size} height={size} viewBox="0 0 80 80" style={{ overflow: "visible" }}>
      <defs>
        {/* Canvas surface — near-white with soft rose tint */}
        <linearGradient id="crt-top" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%"   stopColor="#fff1f2"/>
          <stop offset="100%" stopColor="#fecdd3"/>
        </linearGradient>
        {/* Left depth face — deep rose */}
        <linearGradient id="crt-left" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%"   stopColor="#e11d48"/>
          <stop offset="100%" stopColor="#881337"/>
        </linearGradient>
        {/* Right depth face — rose → coral */}
        <linearGradient id="crt-right" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%"   stopColor="#f43f5e"/>
          <stop offset="100%" stopColor="#fb923c"/>
        </linearGradient>
        {/* Rainbow stroke on canvas */}
        <linearGradient id="crt-stroke" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%"   stopColor="#a855f7"/>
          <stop offset="45%"  stopColor="#06b6d4"/>
          <stop offset="100%" stopColor="#10b981"/>
        </linearGradient>
        {/* Pen barrel gradient */}
        <linearGradient id="crt-pen" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%"   stopColor="#fda4af"/>
          <stop offset="100%" stopColor="#f43f5e"/>
        </linearGradient>
        {glow && (
          <filter id="crt-glow" x="-40%" y="-40%" width="180%" height="180%">
            <feGaussianBlur stdDeviation={3.5 * s} result="blur"/>
            <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
        )}
      </defs>
      <g
        filter={glow ? "url(#crt-glow)" : undefined}
        style={animated ? { animation: "float 3.5s ease-in-out 0.75s infinite" } : {}}
      >
        {/* ── Isometric canvas / artboard ── */}
        {/* Top face (the design surface) */}
        <polygon
          points={`${13*s},${36*s} ${40*s},${19*s} ${67*s},${36*s} ${40*s},${53*s}`}
          fill="url(#crt-top)"
        />
        {/* Left depth face */}
        <polygon
          points={`${13*s},${36*s} ${40*s},${53*s} ${40*s},${66*s} ${13*s},${49*s}`}
          fill="url(#crt-left)"
        />
        {/* Right depth face */}
        <polygon
          points={`${40*s},${53*s} ${67*s},${36*s} ${67*s},${49*s} ${40*s},${66*s}`}
          fill="url(#crt-right)"
        />

        {/* ── Rainbow brush stroke painted on the canvas ── */}
        <path
          d={`M ${22*s},${44*s} C ${30*s},${28*s} ${48*s},${25*s} ${58*s},${33*s}`}
          fill="none"
          stroke="url(#crt-stroke)"
          strokeWidth={3.8 * s}
          strokeLinecap="round"
          opacity="0.88"
        />

        {/* ── Pen / stylus — rotated, hovering upper-right of canvas ── */}
        <g transform={`translate(${64*s},${23*s}) rotate(28)`}>
          {/* Pen barrel */}
          <rect
            x={-2.2 * s} y={-12 * s}
            width={4.4 * s} height={14 * s}
            rx={2.2 * s}
            fill="url(#crt-pen)"
          />
          {/* Highlight stripe on barrel */}
          <rect
            x={0.6 * s} y={-11 * s}
            width={1.2 * s} height={9 * s}
            rx={0.6 * s}
            fill="rgba(255,255,255,0.45)"
          />
          {/* Conical nib */}
          <polygon
            points={`${-2.2*s},${2*s} ${2.2*s},${2*s} ${0},${8*s}`}
            fill="#f43f5e"
          />
          {/* Ink dot at nib tip */}
          <circle cx="0" cy={8.5 * s} r={1.2 * s} fill="#10b981"/>
        </g>

        {/* ── Canvas edge highlight ── */}
        <polygon
          points={`${13*s},${36*s} ${40*s},${19*s} ${67*s},${36*s} ${40*s},${53*s}`}
          fill="none"
          stroke="rgba(255,255,255,0.35)"
          strokeWidth={s}
        />

        {/* ── Sparkle accent dots ── */}
        <circle cx={9*s}  cy={29*s} r={2.2*s} fill="#f43f5e"  opacity="0.75"/>
        <circle cx={71*s} cy={28*s} r={1.6*s} fill="#fb923c"  opacity="0.65"/>
        <circle cx={40*s} cy={11*s} r={1.8*s} fill="#a855f7"  opacity="0.70"/>
      </g>
    </svg>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* CINEMA MARK — Film projector with amber beam                                */
/* ═══════════════════════════════════════════════════════════════════════════ */
function CinemaMark({ size = 80, glow = false, animated = false }) {
  const s = size / 80;
  return (
    <svg width={size} height={size} viewBox="0 0 80 80" style={{ overflow: "visible" }}>
      <defs>
        <linearGradient id="cin-beam" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#f59e0b" stopOpacity="0.85"/>
          <stop offset="100%" stopColor="#f59e0b" stopOpacity="0.04"/>
        </linearGradient>
        <linearGradient id="cin-body" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#fcd34d"/>
          <stop offset="100%" stopColor="#b45309"/>
        </linearGradient>
        <linearGradient id="cin-lens" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#818cf8"/>
          <stop offset="100%" stopColor="#4f46e5"/>
        </linearGradient>
        <radialGradient id="cin-reel" cx="50%" cy="40%" r="60%">
          <stop offset="0%" stopColor="#fde68a"/>
          <stop offset="100%" stopColor="#92400e"/>
        </radialGradient>
        {glow && (
          <filter id="cin-glow" x="-40%" y="-40%" width="180%" height="180%">
            <feGaussianBlur stdDeviation={3.5 * s} result="blur"/>
            <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
        )}
      </defs>
      <g
        filter={glow ? "url(#cin-glow)" : undefined}
        style={animated ? { animation: "float 3.5s ease-in-out 0.6s infinite" } : {}}
      >
        {/* Projector light beam */}
        <polygon
          points={`${44*s},${38*s} ${76*s},${18*s} ${76*s},${58*s}`}
          fill="url(#cin-beam)"
        />
        {/* Top reel */}
        <circle cx={20*s} cy={18*s} r={10*s} fill="#0f0f1a" stroke="#f59e0b" strokeWidth={2*s}/>
        <circle cx={20*s} cy={18*s} r={4.5*s} fill="url(#cin-reel)" opacity="0.9"/>
        <circle cx={20*s} cy={18*s} r={1.8*s} fill="#f59e0b" opacity="0.7"/>
        {/* Bottom reel */}
        <circle cx={20*s} cy={62*s} r={10*s} fill="#0f0f1a" stroke="#f59e0b" strokeWidth={2*s}/>
        <circle cx={20*s} cy={62*s} r={4.5*s} fill="url(#cin-reel)" opacity="0.9"/>
        <circle cx={20*s} cy={62*s} r={1.8*s} fill="#f59e0b" opacity="0.7"/>
        {/* Projector body */}
        <rect x={8*s} y={30*s} width={36*s} height={20*s} rx={5*s} fill="url(#cin-body)"/>
        {/* Film strip teeth top */}
        {[0,1,2,3].map(i => (
          <rect key={i} x={(10+i*8)*s} y={27*s} width={5*s} height={4*s} rx={s} fill="#f59e0b" opacity="0.9"/>
        ))}
        {/* Film strip teeth bottom */}
        {[0,1,2,3].map(i => (
          <rect key={i} x={(10+i*8)*s} y={49*s} width={5*s} height={4*s} rx={s} fill="#f59e0b" opacity="0.9"/>
        ))}
        {/* Lens barrel */}
        <circle cx={44*s} cy={40*s} r={9*s} fill="url(#cin-lens)"/>
        <circle cx={44*s} cy={40*s} r={5.5*s} fill="#030309" opacity="0.85"/>
        <circle cx={41.5*s} cy={37.5*s} r={1.8*s} fill="white" opacity="0.45"/>
        {/* Accent dot */}
        <circle cx={6*s} cy={40*s} r={2*s} fill="#f59e0b" opacity="0.6"/>
      </g>
    </svg>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* STAR FIELD CANVAS                                                           */
/* ═══════════════════════════════════════════════════════════════════════════ */
function StarField({ phase }) {
  const canvasRef = useRef(null);
  const starsRef  = useRef([]);
  const rafRef    = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const resize = () => {
      canvas.width  = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const W = () => canvas.width;
    const H = () => canvas.height;

    /* Init stars */
    starsRef.current = Array.from({ length: 200 }, () => ({
      x: (Math.random() - 0.5) * 2,
      y: (Math.random() - 0.5) * 2,
      z: Math.random(),
      hue: Math.random() < 0.3 ? 270 : Math.random() < 0.5 ? 180 : 220,
    }));

    let lastT = 0;
    const draw = (t) => {
      rafRef.current = requestAnimationFrame(draw);
      const dt = Math.min((t - lastT) / 1000, 0.05);
      lastT = t;

      ctx.clearRect(0, 0, W(), H());

      /* Speed varies by phase */
      const speed = phase <= 1 ? 0.3 : phase === 2 ? 2.5 : phase === 3 ? 1.2 : 0.25;

      starsRef.current.forEach((star) => {
        star.z -= speed * dt;
        if (star.z <= 0) {
          star.x = (Math.random() - 0.5) * 2;
          star.y = (Math.random() - 0.5) * 2;
          star.z = 1;
        }

        const sx = (star.x / star.z) * W() * 0.5 + W() * 0.5;
        const sy = (star.y / star.z) * H() * 0.5 + H() * 0.5;
        const r  = Math.max(0.3, (1 - star.z) * 3.5);
        const alpha = Math.min(1, (1 - star.z) * 1.8);

        ctx.beginPath();
        ctx.arc(sx, sy, r, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${star.hue}, 80%, 80%, ${alpha * 0.7})`;
        ctx.fill();

        /* Speed streaks during warp */
        if (speed > 1.5 && alpha > 0.3) {
          const pz  = star.z + speed * dt;
          const psx = (star.x / pz) * W() * 0.5 + W() * 0.5;
          const psy = (star.y / pz) * H() * 0.5 + H() * 0.5;
          ctx.beginPath();
          ctx.moveTo(sx, sy);
          ctx.lineTo(psx, psy);
          ctx.strokeStyle = `hsla(${star.hue}, 80%, 80%, ${alpha * 0.3})`;
          ctx.lineWidth = r * 0.6;
          ctx.stroke();
        }
      });
    };
    rafRef.current = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
    };
  }, [phase]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 pointer-events-none"
      style={{ opacity: phase >= 4 ? 0.5 : phase >= 2 ? 0.85 : 0.35 }}
    />
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* WORMHOLE RING                                                               */
/* ═══════════════════════════════════════════════════════════════════════════ */
function WormholeRing({ phase }) {
  if (phase < 1 || phase > 3) return null;
  const scale = phase === 1 ? 0.12 : phase === 2 ? 1.7 : 0.04;
  const opacity = phase === 1 ? 1 : phase === 2 ? 0.65 : 0;
  const RING_DEFS = [
    { inset: 0,   col: "rgba(251,191,36,0.75)",  w: 2,   glow: "0 0 34px rgba(251,191,36,0.55), inset 0 0 26px rgba(251,191,36,0.2)"  },
    { inset: 26,  col: "rgba(139,92,246,0.85)",  w: 2.4, glow: "0 0 36px rgba(139,92,246,0.65), inset 0 0 30px rgba(139,92,246,0.22)" },
    { inset: 54,  col: "rgba(6,182,212,0.55)",   w: 1.4, glow: "0 0 24px rgba(6,182,212,0.4)"  },
    { inset: 84,  col: "rgba(192,132,252,0.35)", w: 1,   glow: "none" },
    { inset: 118, col: "rgba(251,191,36,0.18)",  w: 1,   glow: "none" },
  ];
  return (
    <div
      className="absolute pointer-events-none"
      style={{
        left: "50%", top: "50%",
        transform: `translate(-50%, -50%) scale(${scale})`,
        opacity,
        transition: phase === 2
          ? "transform 0.85s cubic-bezier(0.22,1,0.36,1), opacity 0.5s ease"
          : "transform 0.5s ease-out, opacity 0.4s ease",
        width: 400, height: 400,
      }}
    >
      {RING_DEFS.map((r, i) => (
        <div
          key={i}
          className="absolute rounded-full border"
          style={{
            inset: r.inset,
            borderColor: r.col,
            borderWidth: r.w,
            boxShadow: r.glow,
            animation: `orbit-ring ${2 + i * 0.6}s linear ${i % 2 === 0 ? "" : "reverse"} infinite`,
          }}
        />
      ))}
      {/* Dashed HUD energy-arc rings — thin, fast, layered on top for tech detail */}
      {[0, 1].map((i) => (
        <div
          key={`arc-${i}`}
          className="absolute rounded-full"
          style={{
            inset: 14 + i * 42,
            border: `${1}px dashed ${i === 0 ? "rgba(255,255,255,0.4)" : "rgba(34,211,238,0.35)"}`,
            animation: `orbit-ring ${1.2 + i * 0.5}s linear ${i % 2 === 0 ? "reverse" : ""} infinite`,
          }}
        />
      ))}
      {/* Center glow — tri-colour core matching the Nexus Gateway palette */}
      <div
        className="absolute rounded-full"
        style={{
          inset: "50%", transform: "translate(-50%, -50%)",
          width: 70, height: 70,
          background: "radial-gradient(circle, rgba(255,251,235,0.95) 0%, rgba(251,191,36,0.7) 35%, rgba(139,92,246,0.6) 65%, transparent 100%)",
          filter: "blur(9px)",
        }}
      />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* CHARGE BEAMS — CRT-style power-on wings during the charge phase (1)          */
/* ═══════════════════════════════════════════════════════════════════════════ */
function ChargeBeams() {
  return (
    <div className="pointer-events-none absolute inset-0 flex items-center justify-center" style={{ zIndex: 5 }}>
      {/* horizontal light wings expanding out of the spark */}
      <div className="absolute h-px" style={{ width: "72vw", background: "linear-gradient(90deg, transparent, rgba(139,92,246,0.9), #fff, rgba(34,211,238,0.9), transparent)", boxShadow: "0 0 22px 3px rgba(139,92,246,0.5)", animation: "core-charge-wing 0.55s cubic-bezier(0.16,1,0.3,1) both" }} />
      <div className="absolute w-px" style={{ height: "42vh", background: "linear-gradient(180deg, transparent, rgba(251,191,36,0.8), #fff, rgba(251,191,36,0.8), transparent)", boxShadow: "0 0 18px 2px rgba(251,191,36,0.4)", animation: "core-charge-wing 0.55s cubic-bezier(0.16,1,0.3,1) 0.12s both" }} />
      {/* the igniting spark itself */}
      <div className="absolute rounded-full" style={{ width: 22, height: 22, background: "radial-gradient(circle, #fff, rgba(251,191,36,0.7) 45%, transparent 75%)", filter: "blur(1px)", animation: "core-charge-spark 0.6s cubic-bezier(0.34,1.56,0.64,1) both" }} />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* IGNITION BURST — particle shrapnel + shockrings at the logo lock-in (3)      */
/* ═══════════════════════════════════════════════════════════════════════════ */
const BURST_COLORS = ["#8b5cf6", "#22d3ee", "#fbbf24", "#ec4899", "#ffffff", "#a78bfa"];
const BURST = Array.from({ length: 18 }, (_, i) => ({
  angle: `${(i * 20 + (i % 3) * 7) % 360}deg`,
  dist: `${120 + ((i * 53) % 150)}px`,
  size: 3 + (i % 3) * 2,
  color: BURST_COLORS[i % BURST_COLORS.length],
  delay: (i % 5) * 0.03,
}));

function IgnitionBurst() {
  return (
    <div className="pointer-events-none absolute inset-0 flex items-center justify-center" style={{ zIndex: 6 }}>
      {/* debris flying outward (reuses the launch-particle cos/sin keyframe) */}
      {BURST.map((p, i) => (
        <div key={i} className="absolute rounded-full" style={{
          width: p.size, height: p.size, background: p.color,
          boxShadow: `0 0 8px ${p.color}`,
          "--angle": p.angle, "--dist": p.dist,
          animation: `launch-particle 0.9s cubic-bezier(0.16,1,0.3,1) ${p.delay}s both`,
        }} />
      ))}
      {/* double shockring */}
      {[0, 1].map((i) => (
        <div key={`r${i}`} className="absolute rounded-full" style={{
          width: 130, height: 130,
          border: `${2 - i}px solid ${i === 0 ? "rgba(255,255,255,0.9)" : "rgba(139,92,246,0.7)"}`,
          boxShadow: i === 0 ? "0 0 30px rgba(251,191,36,0.5)" : "none",
          animation: `core-shockring 0.9s cubic-bezier(0.16,1,0.3,1) ${i * 0.12}s both`,
        }} />
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* BOOT HUD — the cinematic wrapper around phases 1–3: letterbox bars,          */
/* a live terminal readout with charge-%, hyperspace streaks during warp,      */
/* and the re-wired ChargeBeams / WormholeRing / IgnitionBurst overlays.       */
/* ═══════════════════════════════════════════════════════════════════════════ */
const BOOT_LINES = [
  "SINGULARITÄT ERFASST",
  "MATERIE-KOLLAPS EINGELEITET",
  "SSO-GITTER — ONLINE",
  "KIDS-SAFE PROTOKOLL — AKTIV",
  "11 APPS ANGEDOCKT",
  "GATEWAY-ZÜNDUNG",
];
const BOOT_TOTAL_MS = 3500; // BootHud mount (phase 1, 850ms) → bridge reveal (4350ms)
const SCRAMBLE = "ABCDEFX0123456789#$%&◢◣";

/* Film-studio pretitle — the very first frame of the shot (phase 0) */
function PreTitle() {
  return (
    <div className="pointer-events-none fixed inset-0 grid place-items-center" style={{ zIndex: 50, background: "#000" }}>
      <style>{`
        @keyframes core-pretitle {
          0%   { opacity: 0; letter-spacing: 26px; filter: blur(8px); }
          35%  { opacity: 1; letter-spacing: 14px; filter: blur(0px); }
          75%  { opacity: 1; letter-spacing: 12px; }
          100% { opacity: 0; letter-spacing: 11px; filter: blur(3px); }
        }
        @keyframes core-pretitle-line {
          0%, 20% { transform: scaleX(0); opacity: 0; }
          50%     { transform: scaleX(1); opacity: 1; }
          100%    { transform: scaleX(1); opacity: 0; }
        }
      `}</style>
      <div className="flex flex-col items-center gap-3">
        <div style={{
          fontSize: 13, fontWeight: 800, color: "rgba(226,232,240,0.92)",
          animation: "core-pretitle 0.85s ease both", textIndent: 14,
        }}>
          STUDOX PRÄSENTIERT
        </div>
        <div style={{
          width: 180, height: 1,
          background: "linear-gradient(90deg, transparent, rgba(139,92,246,0.9), transparent)",
          animation: "core-pretitle-line 0.85s ease both",
        }} />
      </div>
    </div>
  );
}

/* Particle wordmark — "STUDOX CORE" assembles from ~1500 flying particles,
   sampled from real rendered text pixels, then the crisp gradient wordmark
   fades in over the settled particle field. */
function ParticleWordmark({ text = "STUDOX CORE" }) {
  const ref = useRef(null);
  const [dims] = useState(() => {
    const W = Math.min(560, (typeof window !== "undefined" ? window.innerWidth : 600) - 48);
    return { W, H: Math.round(W / 6.2), fontPx: Math.round(W / 11.2) };
  });
  useEffect(() => {
    const cv = ref.current;
    if (!cv) return;
    const { W, H, fontPx } = dims;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    cv.width = W * dpr; cv.height = H * dpr;
    cv.style.width = `${W}px`; cv.style.height = `${H}px`;
    const ctx = cv.getContext("2d");
    ctx.scale(dpr, dpr);

    /* sample the wordmark's pixels from an offscreen render */
    const off = document.createElement("canvas");
    off.width = W; off.height = H;
    const octx = off.getContext("2d");
    octx.font = `900 ${fontPx}px system-ui, -apple-system, sans-serif`;
    octx.textAlign = "center"; octx.textBaseline = "middle";
    octx.fillStyle = "#fff";
    octx.fillText(text, W / 2, H / 2 + 2);
    const img = octx.getImageData(0, 0, W, H).data;
    const step = 2;
    const parts = [];
    for (let y = 0; y < H; y += step) {
      for (let x = 0; x < W; x += step) {
        if (img[(y * W + x) * 4 + 3] > 128) {
          const a = Math.random() * Math.PI * 2;
          const r = 130 + Math.random() * 320;
          parts.push({
            tx: x, ty: y,
            x: W / 2 + Math.cos(a) * r, y: H / 2 + Math.sin(a) * r,
            delay: Math.random() * 0.28, u: x / W,
          });
        }
      }
    }

    const COLORS = ["#fde68a", "#e6b1fc", "#c084fc", "#8b5cf6", "#38cff0", "#22d3ee"];
    const t0 = performance.now();
    let raf;
    const tick = (now) => {
      const T = (now - t0) / 1000;
      ctx.clearRect(0, 0, W, H);
      ctx.globalCompositeOperation = "lighter";
      let done = true;
      for (const p of parts) {
        let u = (T - p.delay) / 0.85;
        if (u < 1) done = false;
        u = Math.max(0, Math.min(1, u));
        const e = 1 - Math.pow(1 - u, 3);
        const x = p.x + (p.tx - p.x) * e;
        const y = p.y + (p.ty - p.y) * e;
        ctx.globalAlpha = 0.2 + 0.8 * e;
        ctx.fillStyle = COLORS[Math.min(COLORS.length - 1, Math.floor(p.u * COLORS.length))];
        ctx.fillRect(x, y, 1.4, 1.4);
      }
      if (!done) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [text, dims]);

  /* particles settle → canvas fades OUT while the pixel-perfect gradient
     wordmark (same font, same box) fades IN — clean crossfade, no ghosting */
  return (
    <div style={{ position: "relative" }}>
      <style>{`
        @keyframes core-wm-out { to { opacity: 0; } }
        @keyframes core-wm-in  { to { opacity: 1; } }
      `}</style>
      <canvas ref={ref} style={{ display: "block", animation: "core-wm-out 0.45s ease 1.0s forwards" }} />
      <div style={{
        position: "absolute", inset: 0, display: "grid", placeItems: "center",
        opacity: 0, animation: "core-wm-in 0.45s ease 0.9s forwards",
        filter: "drop-shadow(0 0 20px rgba(139,92,246,0.55)) drop-shadow(0 0 46px rgba(34,211,238,0.25))",
      }}>
        <span style={{
          font: `900 ${dims.fontPx}px system-ui, -apple-system, sans-serif`,
          whiteSpace: "nowrap", lineHeight: 1,
          background: "linear-gradient(90deg,#fde68a,#e6b1fc,#c084fc,#8b5cf6,#38cff0,#22d3ee)",
          WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
        }}>
          {text}
        </span>
      </div>
    </div>
  );
}

const WARP_STREAKS = Array.from({ length: 26 }, (_, i) => ({
  angle: (i * 137.5) % 360,               // golden-angle spread — even but organic
  delay: (i % 7) * 0.06,
  len: 90 + ((i * 61) % 130),
  from: 60 + ((i * 37) % 120),
}));

function BootHud({ phase }) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const start = Date.now();
    const iv = setInterval(() => {
      const el = Date.now() - start;
      setElapsed(el);
      if (el > BOOT_TOTAL_MS + 200) clearInterval(iv);
    }, 50);
    return () => clearInterval(iv);
  }, []);

  const pct = Math.min(100, Math.floor((elapsed / BOOT_TOTAL_MS) * 100));
  const shown = Math.min(BOOT_LINES.length, 1 + Math.floor(elapsed / 480));

  return (
    <div className="pointer-events-none fixed inset-0" style={{ zIndex: 50 }}>
      <style>{`
        @keyframes core-letterbox-in { from { transform: scaleY(0); } to { transform: scaleY(1); } }
        @keyframes core-line-in { from { opacity: 0; transform: translateX(-10px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes core-caret { 0%,49% { opacity: 1; } 50%,100% { opacity: 0; } }
        @keyframes core-warp-streak {
          0%   { opacity: 0; transform: rotate(var(--wa)) translateX(var(--wf)) scaleX(0.1); }
          25%  { opacity: 0.9; }
          100% { opacity: 0; transform: rotate(var(--wa)) translateX(calc(var(--wf) * 4.5)) scaleX(1.5); }
        }
        @keyframes core-logo-drop {
          0%   { opacity: 0; transform: scale(2.6); filter: blur(16px); }
          55%  { opacity: 1; transform: scale(0.94); filter: blur(0px); }
          75%  { transform: scale(1.05); }
          100% { opacity: 1; transform: scale(1); filter: blur(0px); }
        }
        @keyframes core-boot-char2 {
          0%   { opacity: 0; transform: translateY(16px) scale(1.5); filter: blur(7px); }
          100% { opacity: 1; transform: translateY(0) scale(1); filter: blur(0px); }
        }
      `}</style>

      {/* cinematic letterbox bars */}
      {[{ top: 0, transformOrigin: "top" }, { bottom: 0, transformOrigin: "bottom" }].map((pos, i) => (
        <div key={i} style={{
          position: "absolute", left: 0, right: 0, height: "7vh", background: "#000",
          ...pos, animation: "core-letterbox-in 0.5s cubic-bezier(0.16,1,0.3,1) both",
        }} />
      ))}

      {/* terminal boot readout — bottom left, characters DECODE into place */}
      <div style={{
        position: "absolute", left: "max(16px, 3vw)", bottom: "calc(7vh + 18px)",
        fontFamily: "monospace", fontSize: 10.5, lineHeight: 1.9, letterSpacing: 0.8,
      }}>
        {BOOT_LINES.slice(0, shown).map((line, i) => {
          const isLast = i === shown - 1;
          const pending = isLast && pct < 97;
          /* per-line decode: settled prefix + scrambling tail */
          const lp = Math.max(0, Math.min(1, (elapsed - i * 480) / 340));
          const settled = Math.floor(lp * line.length);
          const shownText = line.slice(0, settled) + line.slice(settled).split("")
            .map((ch) => (ch === " " ? " " : SCRAMBLE[Math.floor(Math.random() * SCRAMBLE.length)])).join("");
          return (
            <div key={i} style={{
              color: pending ? "rgba(196,181,253,0.92)" : "rgba(148,163,184,0.55)",
              animation: "core-line-in 0.3s ease both",
              textShadow: pending ? "0 0 12px rgba(139,92,246,0.6)" : "none",
            }}>
              <span style={{ color: pending ? "#fbbf24" : "#22c55e", marginRight: 7 }}>{pending ? "▸" : "✓"}</span>
              {shownText}
              {pending && <span style={{ animation: "core-caret 0.8s step-end infinite", marginLeft: 2 }}>_</span>}
            </div>
          );
        })}
      </div>

      {/* charge percentage — bottom right */}
      <div style={{
        position: "absolute", right: "max(16px, 3vw)", bottom: "calc(7vh + 18px)",
        fontFamily: "monospace", textAlign: "right",
      }}>
        <div style={{
          fontSize: 26, fontWeight: 900, letterSpacing: 1,
          background: "linear-gradient(90deg,#fde68a,#c084fc,#22d3ee)",
          WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
        }}>{pct}%</div>
        <div style={{ fontSize: 8.5, letterSpacing: 3, color: "rgba(148,163,184,0.5)", marginTop: 2 }}>GATEWAY-ENERGIE</div>
        <div style={{ width: 120, height: 2, marginTop: 6, marginLeft: "auto", background: "rgba(255,255,255,0.08)", borderRadius: 2, overflow: "hidden" }}>
          <div style={{ width: `${pct}%`, height: "100%", background: "linear-gradient(90deg,#8b5cf6,#22d3ee,#fbbf24)", boxShadow: "0 0 10px rgba(139,92,246,0.8)", transition: "width 0.1s linear" }} />
        </div>
      </div>

      {/* hyperspace streaks + chromatic-aberration fringes — warp phase only */}
      {phase === 2 && (
        <div className="absolute inset-0 flex items-center justify-center" style={{ overflow: "hidden" }}>
          {/* red/cyan lens fringing at the screen edges — dolly-zoom feel */}
          <div className="absolute inset-0" style={{
            background: "radial-gradient(ellipse at 49.2% 50%, transparent 58%, rgba(255,60,90,0.14) 82%, transparent 100%)",
          }} />
          <div className="absolute inset-0" style={{
            background: "radial-gradient(ellipse at 50.8% 50%, transparent 58%, rgba(40,220,255,0.14) 82%, transparent 100%)",
          }} />
          {WARP_STREAKS.map((s, i) => (
            <div key={i} style={{
              position: "absolute", width: s.len, height: 1.5, borderRadius: 2,
              background: "linear-gradient(90deg, transparent, rgba(196,181,253,0.9), rgba(255,255,255,0.95))",
              boxShadow: "0 0 8px rgba(139,92,246,0.7)",
              "--wa": `${s.angle}deg`, "--wf": `${s.from}px`,
              animation: `core-warp-streak 0.65s cubic-bezier(0.4,0,1,1) ${s.delay}s infinite`,
            }} />
          ))}
        </div>
      )}

      {/* re-wired legacy overlays — charge wings, wormhole rings, ignition burst */}
      {phase === 1 && <ChargeBeams />}
      <WormholeRing phase={phase} />
      {phase === 3 && <IgnitionBurst />}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* ACCESS DENIED MODAL — No app access                                         */
/* ═══════════════════════════════════════════════════════════════════════════ */
function AccessDeniedModal({ app, onClose }) {
  const appMeta = {
    clubs: { name: "StudoX Offline Clubs", icon: "🏛️", color: "#10b981", glow: "rgba(16,185,129,0.25)", grad: "linear-gradient(90deg, transparent, #10b981, #2dd4bf, transparent)" },
    step:  { name: "StudoX Step",          icon: "🪜", color: "#f97316", glow: "rgba(249,115,22,0.25)",  grad: "linear-gradient(90deg, transparent, #fbbf24, #f97316, transparent)" },
    flow:  { name: "StudoX Flow",          icon: "🎓", color: "#8b5cf6", glow: "rgba(139,92,246,0.25)",  grad: "linear-gradient(90deg, transparent, #8b5cf6, #06b6d4, transparent)" },
  };
  const m = appMeta[app] || appMeta.flow;
  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center px-4"
      style={{ background: "rgba(0,0,0,0.8)", backdropFilter: "blur(16px)" }}
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-sm rounded-t-3xl sm:rounded-3xl overflow-hidden border"
        style={{
          borderColor: `${m.color}35`,
          background: `radial-gradient(ellipse at 50% 0%, ${m.color}10 0%, rgba(5,5,18,0.99) 70%)`,
          boxShadow: `0 0 60px ${m.glow}, 0 40px 80px rgba(0,0,0,0.7)`,
          animation: "modalSlideIn 0.35s cubic-bezier(0.34,1.56,0.64,1) forwards",
          maxHeight: "90dvh",
          overflowY: "auto",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top accent line */}
        <div className="absolute inset-x-0 top-0 h-0.5" style={{ background: m.grad }} />

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition-all hover:scale-110 active:scale-95"
          style={{ background: "rgba(255,255,255,0.07)", color: "#6b7280" }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" strokeWidth="2.5" stroke="currentColor" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
        </button>

        <div className="p-8 text-center">
          {/* Shield icon */}
          <div className="flex justify-center mb-4">
            <div
              className="w-20 h-20 rounded-2xl flex items-center justify-center relative"
              style={{ background: `${m.color}15`, border: `1.5px solid ${m.color}30` }}
            >
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" strokeWidth="1.5" stroke={m.color} strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                <path d="M9 12l2 2 4-4" strokeWidth="2"/>
              </svg>
              <div
                className="absolute inset-0 rounded-2xl"
                style={{ background: `radial-gradient(circle at 50% 50%, ${m.color}20, transparent 70%)`, filter: "blur(8px)" }}
              />
            </div>
          </div>

          <h2 className="text-xl font-black tracking-tight text-white mb-1">Kein Zugang</h2>
          <p className="text-sm font-bold mb-3" style={{ color: m.color }}>{m.icon} {m.name}</p>

          <p className="text-sm text-gray-400 leading-relaxed">
            Dein Account hat aktuell keinen Zugang zu <strong className="text-gray-300">{m.name}</strong>.
            Bitte wende dich an deinen Administrator und bitte um einen Code mit dem entsprechenden App-Zugang.
          </p>

          {/* Info box */}
          <div
            className="mt-4 p-3 rounded-2xl text-left"
            style={{ background: `${m.color}08`, border: `1px solid ${m.color}18` }}
          >
            <p className="text-[11px] text-gray-500 leading-relaxed">
              💡 Admins können im Dashboard unter <span className="text-gray-400 font-bold">Code erstellen → App-Zugang</span> gezielt Apps für neue Codes freischalten.
            </p>
          </div>

          <button
            onClick={onClose}
            className="mt-5 w-full py-3 rounded-2xl font-black text-sm transition-all hover:scale-[1.02] active:scale-[0.98]"
            style={{
              background: `linear-gradient(135deg, ${m.color}30, ${m.color}18)`,
              border: `1px solid ${m.color}40`,
              color: m.color,
            }}
          >
            Verstanden
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* NOTIFY TOAST — Step notification confirmation                               */
/* ═══════════════════════════════════════════════════════════════════════════ */
function NotifyToast({ onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 3000); return () => clearTimeout(t); }, [onDone]);
  return (
    <div
      className="fixed bottom-6 left-1/2 z-[70] flex items-center gap-3 px-5 py-3.5 rounded-2xl border"
      style={{
        transform: "translateX(-50%)",
        background: "rgba(8,8,24,0.95)",
        borderColor: "rgba(251,191,36,0.4)",
        boxShadow: "0 0 30px rgba(251,191,36,0.2), 0 20px 40px rgba(0,0,0,0.5)",
        backdropFilter: "blur(20px)",
        animation: "toastIn 0.4s cubic-bezier(0.34,1.56,0.64,1) forwards",
      }}
    >
      <span className="text-xl">🔔</span>
      <div>
        <p className="text-sm font-black text-white">Benachrichtigung aktiviert!</p>
        <p className="text-xs text-gray-500">Du erfährst als Erster wenn StudoX Step startet.</p>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* COMING SOON MODAL — StudoX Step                                             */
/* ═══════════════════════════════════════════════════════════════════════════ */
function ComingSoonModal({ onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center px-4"
      style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(12px)" }}
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-sm rounded-t-3xl sm:rounded-3xl overflow-hidden border animate-nd-in"
        style={{
          borderColor: "rgba(251,191,36,0.3)",
          background: "radial-gradient(ellipse at 50% 0%, rgba(251,191,36,0.12) 0%, rgba(8,8,24,0.98) 70%)",
          boxShadow: "0 0 60px rgba(251,191,36,0.15), 0 40px 80px rgba(0,0,0,0.6)",
          maxHeight: "90dvh",
          overflowY: "auto",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top accent */}
        <div className="absolute inset-x-0 top-0 h-0.5" style={{ background: "linear-gradient(90deg, transparent, #fbbf24, #f97316, transparent)" }} />

        <div className="p-8 text-center">
          {/* Logo */}
          <div className="flex justify-center mb-5">
            <div style={{ filter: "drop-shadow(0 0 20px rgba(251,191,36,0.5)) drop-shadow(0 0 40px rgba(249,115,22,0.3))" }}>
              <StepMark size={90} glow animated />
            </div>
          </div>

          <h2 className="text-2xl font-black tracking-tight mb-1">StudoX Step</h2>
          <p className="text-sm font-black mb-1" style={{
            background: "linear-gradient(90deg, #fbbf24, #f97316, #ef4444)",
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
          }}>
            Bald verfügbar
          </p>

          <p className="text-sm text-gray-400 mt-3 leading-relaxed">
            Spielend lernen für Erstklässler. StudoX Step bringt Lesen, Schreiben und Rechnen in eine magische Abenteuerwelt für Kinder zwischen 6 und 10 Jahren.
          </p>

          {/* Features preview */}
          <div className="mt-5 space-y-2 text-left">
            {[
              { icon: "🎨", text: "Bunte Animationen & Charaktere" },
              { icon: "🎯", text: "Altersgerechte Mini-Games" },
              { icon: "👨‍👩‍👧", text: "Eltern-Fortschritts-App" },
              { icon: "🏫", text: "Speziell für 1.–4. Volksschulklasse" },
            ].map((f) => (
              <div key={f.icon} className="flex items-center gap-3 px-3 py-2 rounded-xl" style={{ background: "rgba(251,191,36,0.05)", border: "1px solid rgba(251,191,36,0.1)" }}>
                <span className="text-base">{f.icon}</span>
                <span className="text-xs text-gray-400">{f.text}</span>
              </div>
            ))}
          </div>

          {/* Notify button */}
          <button
            className="mt-5 w-full py-3.5 rounded-2xl font-black text-sm transition-all hover:scale-[1.02] active:scale-[0.98]"
            style={{
              background: "linear-gradient(135deg, #fbbf24, #f97316)",
              boxShadow: "0 0 20px rgba(251,191,36,0.3), 0 8px 20px rgba(0,0,0,0.4)",
              color: "#000",
            }}
            onClick={() => { onClose(); setTimeout(() => window.dispatchEvent(new CustomEvent("studox-step-notify")), 50); }}
          >
            🔔 Benachrichtigung aktivieren
          </button>

          <button onClick={onClose} className="mt-3 text-xs text-gray-600 hover:text-gray-400 transition-colors">
            Schließen
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* APP CARD                                                                    */
/* ═══════════════════════════════════════════════════════════════════════════ */
function AppCard({ app, delay = 0, phase, onLaunch }) {
  const [hovered, setHovered] = useState(false);
  const visible = phase >= 4;

  return (
    <div
      className={`relative rounded-3xl overflow-hidden border cursor-pointer transition-all duration-500 select-none ${hovered ? "holographic" : ""}`}
      style={{
        borderColor: hovered ? `${app.accentA}60` : `${app.accentA}20`,
        // Solid opaque base FIRST (guarantees legibility everywhere on the card,
        // even against a bright bloom-heavy 3D background) + a pretty accent
        // wash layered on top — never rely on the wash alone for contrast.
        background: hovered
          ? `radial-gradient(ellipse at 50% 0%, ${app.accentA}22 0%, transparent 62%), rgb(9,9,20)`
          : `radial-gradient(ellipse at 50% 0%, ${app.accentA}10 0%, transparent 62%), rgb(8,8,18)`,
        backdropFilter: "blur(28px) saturate(140%)",
        WebkitBackdropFilter: "blur(28px) saturate(140%)",
        boxShadow: hovered
          ? `0 0 40px ${app.accentA}20, 0 20px 40px rgba(0,0,0,0.5), inset 0 1px 0 ${app.accentA}20`
          : `0 8px 32px rgba(0,0,0,0.4)`,
        transform: visible
          ? hovered ? "translateY(-6px) scale(1.01)" : "translateY(0) scale(1)"
          : "translateY(40px) scale(0.95)",
        opacity: visible ? 1 : 0,
        transition: `transform 0.6s cubic-bezier(0.34,1.56,0.64,1) ${delay}ms,
                     opacity 0.5s ease ${delay}ms,
                     box-shadow 0.3s ease,
                     border-color 0.3s ease,
                     background 0.3s ease`,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onLaunch}
    >
      {/* Top gradient line */}
      <div
        className="absolute inset-x-0 top-0 h-0.5 transition-opacity duration-300"
        style={{
          background: `linear-gradient(90deg, transparent, ${app.accentA}, ${app.accentB}, transparent)`,
          opacity: hovered ? 1 : 0.4,
        }}
      />

      {/* Prototype badge */}
      {app.prototype && (
        <div
          className="absolute top-3 right-3 z-20 px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-widest"
          style={{
            color: "#fff",
            background: `linear-gradient(135deg, ${app.accentA}, ${app.accentB})`,
            boxShadow: `0 0 16px ${app.accentA}55`,
            border: "1px solid rgba(255,255,255,0.25)",
          }}
        >
          ✦ Prototyp
        </div>
      )}

      {/* Coming soon overlay — subtle gradient only, no badge (button already says it) */}
      {app.comingSoon && (
        <div
          className="absolute inset-0 z-10 pointer-events-none"
          style={{ background: "linear-gradient(to top, rgba(0,0,0,0.4) 0%, transparent 50%)" }}
        />
      )}

      <div className="relative z-10 p-6 flex flex-col items-center text-center gap-4">
        {/* Logo */}
        <div
          className="relative transition-transform duration-300"
          style={{
            transform: hovered ? "translateY(-4px)" : "none",
            filter: hovered
              ? `drop-shadow(0 0 24px ${app.accentA}60) drop-shadow(0 0 48px ${app.accentA}30)`
              : `drop-shadow(0 0 12px ${app.accentA}30)`,
          }}
        >
          {(() => {
            const Mark = app.Mark;
            return Mark
              ? <Mark size={80} glow={hovered} animated={hovered} dimmed={app.comingSoon && !hovered} />
              : (
                <div
                  className="flex items-center justify-center rounded-3xl"
                  style={{
                    width: 80, height: 80,
                    background: `linear-gradient(135deg, ${app.accentA}, ${app.accentB})`,
                    boxShadow: hovered ? `0 0 28px ${app.accentA}70, inset 0 2px 6px rgba(255,255,255,0.35)` : `inset 0 2px 6px rgba(255,255,255,0.25)`,
                    fontSize: 38,
                  }}
                >
                  <span style={{ filter: "drop-shadow(0 3px 6px rgba(0,0,0,0.4))" }}>{app.emoji || "✦"}</span>
                </div>
              );
          })()}
        </div>

        {/* Name + tagline */}
        <div>
          <h3
            className="text-xl font-black tracking-tight mb-1 transition-all duration-300"
            style={hovered
              ? { background: `linear-gradient(135deg, ${app.accentA}, ${app.accentB})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.7))" }
              : { color: "#fff", textShadow: "0 2px 8px rgba(0,0,0,0.7)" }
            }
          >
            {app.name}
          </h3>
          <p
            className="text-[11px] font-black uppercase tracking-widest mb-2"
            style={{ color: `${app.accentA}f0`, textShadow: "0 1px 6px rgba(0,0,0,0.7)" }}
          >
            {app.tagline}
          </p>
          <p className="text-xs text-gray-300 leading-relaxed max-w-[200px]" style={{ textShadow: "0 1px 6px rgba(0,0,0,0.75)" }}>
            {app.description}
          </p>
        </div>

        {/* Audience pills */}
        <div className="flex flex-wrap justify-center gap-1.5">
          {app.badges.map((badge) => (
            <span
              key={badge}
              className="px-2.5 py-0.5 rounded-full text-[10px] font-bold border"
              style={{ borderColor: `${app.accentA}25`, background: `${app.accentA}08`, color: `${app.accentA}90` }}
            >
              {badge}
            </span>
          ))}
        </div>

        {/* Launch button */}
        <button
          className="w-full py-3 rounded-2xl font-black text-sm transition-all duration-200 hover:scale-[1.03] active:scale-[0.97]"
          style={app.comingSoon
            ? { border: `1px solid ${app.accentA}30`, color: `${app.accentA}70`, background: `${app.accentA}05` }
            : {
                background: hovered
                  ? `linear-gradient(135deg, ${app.accentA}, ${app.accentB})`
                  : `linear-gradient(135deg, ${app.accentA}90, ${app.accentB}90)`,
                boxShadow: hovered ? `0 0 20px ${app.accentA}40, 0 8px 20px rgba(0,0,0,0.4)` : "none",
                color: app.id === "step" ? "#000" : "#fff",
              }
          }
        >
          {app.comingSoon ? "Bald verfügbar" : app.external ? "Öffnen ↗" : "Starten →"}
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* CORE LOGIN MODAL — inline login, no page navigation needed                  */
/* ═══════════════════════════════════════════════════════════════════════════ */
function CoreLoginModal({ onClose, onSuccess, initialCode = "", forced = false }) {
  const { login } = useAuthStore();
  const [form, setForm]       = useState({ name: "", plz: "", code: initialCode || "" });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.plz.trim() || !form.code.trim()) {
      setError("Bitte alle Felder ausfüllen");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await login({ name: form.name.trim(), plz: form.plz.trim(), code: form.code.trim() });
      onSuccess?.();
    } catch (err) {
      setError(err?.response?.data?.error || "Login fehlgeschlagen – Code oder Name prüfen");
    } finally {
      setLoading(false);
    }
  };

  const fields = [
    { key: "name", placeholder: "Vor- und Nachname",  type: "text",     icon: "👤", label: "Name" },
    { key: "plz",  placeholder: "Postleitzahl",        type: "text",     icon: "📍", label: "PLZ" },
    { key: "code", placeholder: "StudoX-Code",           type: "text",     icon: "🔑", label: "Code" },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center px-4 pb-0 sm:pb-4"
      style={{ background: forced ? "rgba(0,0,0,0.92)" : "rgba(0,0,0,0.8)", backdropFilter: "blur(16px)" }}
      onClick={forced ? undefined : onClose}
    >
      <div
        className="relative w-full max-w-sm rounded-t-3xl sm:rounded-3xl overflow-hidden border"
        style={{
          borderColor: "rgba(139,92,246,0.35)",
          background: "radial-gradient(ellipse at 50% 0%, rgba(139,92,246,0.15) 0%, rgba(3,3,16,0.99) 70%)",
          boxShadow: "0 0 60px rgba(139,92,246,0.2), 0 40px 80px rgba(0,0,0,0.7)",
          animation: "nd-in 0.4s cubic-bezier(.34,1.56,.64,1) both",
          maxHeight: "90dvh",
          overflowY: "auto",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top accent line */}
        <div className="absolute inset-x-0 top-0 h-0.5"
          style={{ background: "linear-gradient(90deg, transparent, #8b5cf6, #06b6d4, transparent)" }} />

        <div className="p-7">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div style={{ filter: "drop-shadow(0 0 8px rgba(139,92,246,0.7))" }}>
                  <CoreMark size={28} glow />
                </div>
                <h2 className="text-lg font-black text-white">{forced ? "Naaa du… 👀" : "Anmelden"}</h2>
              </div>
              <p className="text-xs text-gray-500">{forced ? "Vergessen anzumelden, oder? Ohne Login geht's hier nicht weiter 😏" : "mit deinem StudoX-Code"}</p>
            </div>
            {!forced && (
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full flex items-center justify-center text-gray-600 hover:text-gray-400 hover:bg-white/5 transition-all"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
            )}
          </div>

          {/* Invitation banner (when arriving via a custom invite link) */}
          {initialCode && (
            <div
              className="mb-4 flex items-start gap-2.5 rounded-2xl px-3.5 py-3"
              style={{ background: "linear-gradient(135deg, rgba(139,92,246,0.16), rgba(6,182,212,0.16))", border: "1px solid rgba(139,92,246,0.35)" }}
            >
              <span style={{ fontSize: 18 }}>🎟️</span>
              <p className="text-[12px] leading-snug text-gray-200">
                <b className="text-white">Du wurdest eingeladen.</b> Dein Code ist schon eingetragen — gib nur noch Name &amp; PLZ ein.
              </p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-3">
            {fields.map(({ key, placeholder, type, icon, label }) => (
              <div key={key}>
                <label className="block text-[10px] font-bold text-gray-600 uppercase tracking-widest mb-1.5 ml-1">
                  {icon} {label}
                </label>
                <input
                  type={type}
                  placeholder={placeholder}
                  value={form[key]}
                  onChange={set(key)}
                  autoComplete="off"
                  className="w-full px-4 py-3 rounded-2xl text-sm text-white placeholder-gray-600 outline-none transition-all"
                  style={{
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(139,92,246,0.2)",
                  }}
                  onFocus={(e) => { e.target.style.borderColor = "rgba(139,92,246,0.5)"; e.target.style.background = "rgba(139,92,246,0.06)"; }}
                  onBlur={(e)  => { e.target.style.borderColor = "rgba(139,92,246,0.2)"; e.target.style.background = "rgba(255,255,255,0.04)"; }}
                />
              </div>
            ))}

            {error && (
              <div className="px-4 py-2.5 rounded-xl text-xs text-red-400 font-medium"
                style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.2)" }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 rounded-2xl font-black text-sm text-white transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-60 disabled:scale-100 mt-2"
              style={{
                background: loading
                  ? "rgba(139,92,246,0.4)"
                  : "linear-gradient(135deg, #8b5cf6, #06b6d4)",
                boxShadow: loading ? "none" : "0 0 24px rgba(139,92,246,0.4), 0 8px 20px rgba(0,0,0,0.4)",
              }}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                  </svg>
                  Einloggen…
                </span>
              ) : "Einloggen →"}
            </button>
          </form>

          {/* Hint */}
          <p className="mt-4 text-center text-[10px] text-gray-700 leading-relaxed">
            Den Code erhältst du von deinem Lehrer oder deiner Institution.
          </p>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* MAIN COMPONENT                                                              */
/* ═══════════════════════════════════════════════════════════════════════════ */
export default function StudoXCore() {
  const { isAuthenticated, user, token } = useAuthStore();
  const navigate = useNavigate();
  const [phase, setPhase]               = useState(0);
  const [ignitePulse, setIgnitePulse]   = useState(0);
  const [showStepModal, setShowStepModal] = useState(false);
  const [showLogin, setShowLogin]       = useState(false);
  const [pendingLaunch, setPendingLaunch] = useState(null); // "clubs" | null
  const [accessDeniedApp, setAccessDeniedApp] = useState(null); // app id string | null
  const [showNotifyToast, setShowNotifyToast] = useState(false);
  const [showDashboard, setShowDashboard] = useState(false);
  const [inviteCode, setInviteCode]       = useState("");
  const [trialStarted, setTrialStarted]   = useState(false);
  const [trialLocked, setTrialLocked]     = useState(false);

  // ── Trial lock: unauthenticated visitors get ~25s, then an uncloseable login
  //    popup. The bar is subtle; once it locks it STAYS locked (survives refresh).
  useEffect(() => {
    if (isAuthenticated) return;
    if (typeof window === "undefined") return;
    if (localStorage.getItem("studox_trial_locked") === "1") { setTrialLocked(true); return; }
    const start = setTimeout(() => setTrialStarted(true), 60); // kick the progress bar
    const lock = setTimeout(() => {
      try { localStorage.setItem("studox_trial_locked", "1"); } catch {}
      setTrialLocked(true);
    }, 25000);
    return () => { clearTimeout(start); clearTimeout(lock); };
  }, [isAuthenticated]);

  // Custom invitation links (?invite=CODE): pre-fill the code & open login.
  useEffect(() => {
    const code = readInviteCode();
    if (code && !isAuthenticated) {
      setInviteCode(code);
      setShowLogin(true);
    }
    if (code) clearInviteFromURL();
  }, [isAuthenticated]);

  const BACKEND_URL_local = import.meta.env.VITE_API_URL?.replace("/api", "") || "http://localhost:4000";
  const CLUBS_URL   = import.meta.env.VITE_CLUBS_URL   || "https://clubs.studox.eu";
  const FLOW_URL    = import.meta.env.VITE_FLOW_URL    || "https://flow.studox.eu";
  const CREATE_URL  = import.meta.env.VITE_CREATE_URL  || "https://create.studox.eu";
  const CINEMA_URL  = import.meta.env.VITE_CINEMA_URL  || "https://cinema.studox.eu";
  const LYRICS_URL  = import.meta.env.VITE_LYRICS_URL  || "https://lyrics.studox.eu";
  const MEET_URL    = import.meta.env.VITE_MEET_URL    || "https://meet.studox.eu";
  const HELP_URL    = import.meta.env.VITE_HELP_URL    || "https://help.studox.eu";

  /* ── SSO auto-login: ?sso=<nonce> from Flow/Clubs/Step ─────────────────── */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ssoToken = params.get("sso");
    if (!ssoToken) return;

    // Remove ?sso= from URL immediately (clean URL, prevent re-use on refresh)
    const clean = window.location.pathname;
    window.history.replaceState({}, "", clean);

    // Exchange nonce for a full JWT
    fetch(`${BACKEND_URL_local}/api/auth/sso-exchange?token=${encodeURIComponent(ssoToken)}`)
      .then((r) => r.json())
      .then(({ token: jwt, user: u }) => {
        if (jwt && u) {
          useAuthStore.setState({ token: jwt, user: u, isAuthenticated: true });
          writeSSOToken(jwt);
        }
      })
      .catch(() => { /* SSO expired — user just stays logged-out, can log in manually */ });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* Write SSO token so other StudoX apps can pick it up */
  useEffect(() => {
    if (token) writeSSOToken(token);
  }, [token]);

  /* ── ?returnTo= handler: if an app sent us here for SSO, redirect back ── */
  useEffect(() => {
    if (!isAuthenticated || !token) return;
    const params = new URLSearchParams(window.location.search);
    const returnTo = params.get("returnTo");
    if (!returnTo) return;

    // Security: only redirect to known StudoX origins
    const ALLOWED = [
      "https://create.studox.eu",
      "https://flow.studox.eu",
      "https://clubs.studox.eu",
      "https://cinema.studox.eu",
      "https://meet.studox.eu",
      "https://lyrics.studox.eu",
      "https://music.studox.eu",
      "https://events.studox.eu",
      "https://quest.studox.eu",
      "https://mic.studox.eu",
      "https://adulting.studox.eu",
      "https://futurecity.studox.eu",
      "https://startup.studox.eu",
      "http://localhost",
    ];
    if (!ALLOWED.some((o) => returnTo.startsWith(o))) return;

    // Admins & Overseer have NO Flow student app — Flow would only bounce them
    // straight back here (the "thrown back to Core" flicker). Keep them on the
    // Core hub (their real home) instead of the jarring flow↔core round-trip.
    const role = useAuthStore.getState().user?.role;
    if ((role === "admin" || role === "solid_ground") && returnTo.startsWith("https://flow.studox.eu")) {
      window.history.replaceState({}, "", window.location.pathname); // drop returnTo so a refresh won't retry
      return;
    }

    // Generate SSO token and hand it back to the requesting app
    fetch(`${BACKEND_URL_local}/api/auth/sso-token`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({}),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.token) {
          const url = new URL(returnTo);
          url.searchParams.set("sso", data.token);
          window.location.href = url.toString();
        }
      })
      .catch(() => {});
  }, [isAuthenticated, token]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Step notify toast listener */
  useEffect(() => {
    const handler = () => setShowNotifyToast(true);
    window.addEventListener("studox-step-notify", handler);
    return () => window.removeEventListener("studox-step-notify", handler);
  }, []);

  /* ── Entry animation phases ──────────────────────────────────────────── */
  /* Phase 0: black              (0ms)
     Phase 1: portal charge-up   (450ms)
     Phase 2: warp stars         (1050ms)
     Phase 3: gateway ignition + logo lock-in (2050ms) — fires ignitePulse,
              which flashes the Nexus Gateway 3D background in sync
     Phase 4: hub content        (2950ms)  */
  useEffect(() => {
    const t1 = setTimeout(() => setPhase(1), 850);   // pretitle → chaos collapse
    const t2 = setTimeout(() => setPhase(2), 2000);  // warp dive
    const t3 = setTimeout(() => { setPhase(3); setIgnitePulse(Date.now()); }, 3000);
    const t4 = setTimeout(() => setPhase(4), 4350);  // bridge reveal
    return () => [t1, t2, t3, t4].forEach(clearTimeout);
  }, []);

  const APPS = [
    /* ── ⚙️ Motor — Produktivität ─────────────────────────────────────── */
    {
      id: "flow", name: "StudoX Flow", tagline: "Gamified Learning", Mark: FlowLogo,
      description: "Die vollständig gamifizierte Lernplattform für Schulen, Nachhilfeinstitute und Bildungseinrichtungen ab der 4. Klasse.",
      accentA: "#8b5cf6", accentB: "#06b6d4", badges: ["Schüler", "Lehrer", "Eltern", "Mentoren"], comingSoon: false,
    },
    {
      id: "quest", name: "StudoX Quest", tagline: "Missionen & Challenges", Mark: QuestMark, external: true, href: "https://quest.studox.eu",
      description: "Tägliche Missionen, Challenges und Belohnungen — dein Abenteuer durch das StudoX-Ökosystem.",
      accentA: "#22c55e", accentB: "#fbbf24", badges: ["Missionen", "Belohnungen", "Alle"],
    },
    {
      id: "step", name: "StudoX Step", tagline: "Früh starten. Weit kommen.", Mark: StepLogo,
      description: "Spielend Lesen, Schreiben und Rechnen lernen. Speziell entwickelt für Volksschulkinder zwischen 6 und 10 Jahren.",
      accentA: "#f97316", accentB: "#fbbf24", badges: ["1.–4. Klasse", "6–10 Jahre", "Volksschule"], comingSoon: true,
    },

    /* ── 🧠 Hirn — Lernen & Wissen ────────────────────────────────────── */
    {
      id: "cinema", name: "StudoX Cinema", tagline: "Lernen. Schauen. Verstehen.", Mark: CinemaLogo,
      description: "Die Bildungs-Streamingplattform des StudoX Ökosystems — kuratierte Lernvideos für Schüler, Lehrer und Eltern. Jetzt streamen.",
      accentA: "#f59e0b", accentB: "#d97706", badges: ["Schüler", "Lehrer", "Eltern", "8+"], comingSoon: false,
    },
    {
      id: "adulting", name: "ADULTING", tagline: "Crashkurs fürs echte Leben", Mark: AdultingMark,
      description: "Steuern, Mietvertrag, Gehaltszettel, Scams, Amtswege — alles, was die Schule nicht lehrt, als Level-Map mit Simulationen. 89 Level, 100 % Österreich, XP zählt zu deinem Konto.",
      accentA: "#3ddc97", accentB: "#4cc9f0", badges: ["Real-Life", "89 Level", "🇦🇹", "12+"], comingSoon: false,
    },

    {
      id: "startup", name: "StudoX Startup", tagline: "Bau, was es noch nicht gibt", Mark: StartupMark,
      description: "Der Founder-Sandkasten: 6 Stationen von der Idee bis zum Launch — mit echten Gründer-Tools wie Mini-Canvas, Pitch-Builder und Runway-Rechner. Belegte Fakten statt Hustle-Mythen.",
      accentA: "#c6ff4a", accentB: "#3de8ff", badges: ["Entrepreneurship", "6 Stationen", "Tools", "12+"], comingSoon: false,
    },
    {
      id: "futurecity", name: "Future City", tagline: "Die begehbare Stadt des Wissens", Mark: FutureCityMark, external: true, href: "https://futurecity.studox.eu",
      description: "Ein 3D-Lernspiel direkt im Browser: erkunde eine Stadt der Zukunft und sammle Wissen an interaktiven Stationen — Lernen durch Entdecken statt Abfragen.",
      accentA: "#22d3ee", accentB: "#39ff14", badges: ["3D-Spiel", "Browser", "Entdecken"],
    },
    {
      id: "rapvid", name: "StudoX RapVid", tagline: "Das spielbare Musikvideo", Mark: RapVidMark, external: true, href: "https://rapvid.studox.eu",
      description: "Ein Musikvideo, durch das du dich in 3D bewegst — Szene für Szene auf den Track synchronisiert. Kultur trifft Technologie.",
      accentA: "#ff3df0", accentB: "#7b5cff", badges: ["3D", "Musik", "Experience"],
    },

    /* ── ✨ Werkstatt — Create ────────────────────────────────────────── */
    {
      id: "create", name: "StudoX Create", tagline: "Visual Content Studio", Mark: CreateLogo,
      description: "Erstelle visuell beeindruckende Quizzes, Lernmaterialien und Designs — mit einer vollständigen Canva-ähnlichen Visual Engine.",
      accentA: "#f43f5e", accentB: "#fb923c", badges: ["Lehrer", "Admins", "Schüler", "Designs"], comingSoon: false,
    },

    /* ── ❤️ Herz — Community ──────────────────────────────────────────── */
    {
      id: "meet", name: "StudoX Meet", tagline: "Sprich. Sieh. Verbinde.", Mark: MeetMark,
      description: "Das Kommunikations-Herz von StudoX — Text, Sprache und Video in kuratierten Servern von Lehrer:innen, Admins & Overseer. Sicher für Kinder.",
      accentA: "#7c3aed", accentB: "#22d3ee", badges: ["Sprache", "Video", "Chat", "Sicher"], comingSoon: false,
    },
    {
      id: "clubs", name: "Offline Clubs", tagline: "Lerngruppen & Community", Mark: ClubsLogo,
      description: "Erstelle und verwalte Lernclubs, organisiere Studysessions und vernetze dich mit anderen Lernenden in deiner Umgebung.",
      accentA: "#10b981", accentB: "#2dd4bf", badges: ["Schüler", "Studenten", "Lerngruppen", "12+"], comingSoon: false,
    },
    {
      id: "events", name: "StudoX Events", tagline: "Was als Nächstes passiert", Mark: EventsMark, external: true, href: "https://events.studox.eu",
      description: "Alle StudoX-Events, Wettbewerbe und Community-Termine an einem Ort. Sei dabei.",
      accentA: "#fbbf24", accentB: "#f97316", badges: ["Events", "Termine", "Live"],
    },
    {
      id: "discord", name: "StudoX Discord", tagline: "Die Community-Basis", Mark: DiscordMark, external: true, href: "https://discord.studox.eu",
      description: "Tritt der offiziellen StudoX-Discord-Community bei — News, Support und Austausch mit anderen.",
      accentA: "#818cf8", accentB: "#4338ca", badges: ["Community", "Chat"],
    },
    {
      id: "invite", name: "StudoX Invite", tagline: "Bring deine Freunde mit", Mark: InviteMark, external: true, href: "https://invite.studox.eu",
      description: "Lade Freund:innen ein und wachse mit deiner Lern-Crew. Jede Einladung zählt.",
      accentA: "#c4b5fd", accentB: "#7c3aed", badges: ["Einladen", "Wachsen"],
    },

    /* ── 🎭 Seele — Media & Entertainment ─────────────────────────────── */
    {
      id: "music", name: "StudoX Music", tagline: "Dein Sound-Universum", Mark: MusicMark, external: true, href: "https://music.studox.eu",
      description: "Musik zum Lernen, Fokussieren und Abschalten — kuratierte Sounds für jede Stimmung.",
      accentA: "#7c3aed", accentB: "#22d3ee", badges: ["Musik", "Fokus", "Vibes"],
    },
    {
      id: "phonk", name: "StudoX Phonk", tagline: "Cinematic Experience", Mark: PhonkMark, external: true, href: "https://phonk.studox.eu",
      description: "Die cinematische StudoX-Experience — ein begehbares 3D-Universum. Bildung verändert Welten.",
      accentA: "#a78bfa", accentB: "#ec4899", badges: ["3D", "Cinematic", "WASD"],
    },
    {
      id: "lyrics", name: "StudoX Lyrics", tagline: "Schreib deinen Sound", Mark: LyricsMark, external: true, href: "https://lyrics.studox.eu",
      description: "Dein Musik- & Lyrics-Studio: Texte schreiben, Beats fühlen, Songs gestalten. Frisch aus dem Labor — als Prototyp.",
      accentA: "#ec4899", accentB: "#8b5cf6", badges: ["Musik", "Kreativ", "Beta"], prototype: true,
    },
    {
      id: "mic", name: "StudoX Mic", tagline: "Deine Stimme, dein Studio", Mark: MicMark, external: true, href: "https://mic.studox.eu",
      description: "Nimm auf, sprich, performe — das Mikrofon-Studio des StudoX-Ökosystems.",
      accentA: "#f9a8d4", accentB: "#be185d", badges: ["Audio", "Aufnahme"],
    },

    /* ── 🌐 Nervensystem — Main Hub & Navigation ──────────────────────── */
    {
      id: "hub", name: "StudoX Hauptseite", tagline: "Das Herz der Plattform", Mark: HubMark, external: true, href: "https://studox.eu",
      description: "Die offizielle StudoX-Hauptseite — dein Startpunkt in das gesamte Ökosystem.",
      accentA: "#7c3aed", accentB: "#22d3ee", badges: ["Hauptseite", "Start"],
    },
    {
      id: "info", name: "StudoX Info", tagline: "Alles über StudoX", Mark: InfoMark, external: true, href: "https://studox.info",
      description: "Erfahre mehr über die StudoX-Vision, das Team und die Mission hinter allem.",
      accentA: "#a78bfa", accentB: "#6d28d9", badges: ["Über uns", "Vision"],
    },
    {
      id: "impact", name: "StudoX Impact", tagline: "Bildung verändert Welten", Mark: ImpactMark, external: true, href: "https://studox.org",
      description: "Der gemeinnützige Arm von StudoX — unser Beitrag für gerechte Bildung weltweit.",
      accentA: "#fb7185", accentB: "#e11d48", badges: ["Non-Profit", "Impact"],
    },

    /* ── 🛡️ Immunsystem — Support & Status ────────────────────────────── */
    {
      id: "help", name: "StudoX Hilfe", tagline: "Wir sind für dich da", Mark: HelpMark, external: true, href: "https://help.studox.eu",
      description: "Vertrauliche Notfallhilfe für emotionale & schulische Notlagen. Du bist nie allein.",
      accentA: "#fb7185", accentB: "#be123c", badges: ["Notfall", "Vertraulich", "24/7"],
    },
    {
      id: "support", name: "StudoX Support", tagline: "Hilfe & Anliegen", Mark: SupportMark, external: true, href: "https://support.studox.eu",
      description: "Support-Center, FAQ und Einsprüche — hier bekommst du technische & organisatorische Hilfe.",
      accentA: "#67e8f9", accentB: "#0e7490", badges: ["Support", "FAQ"],
    },
    {
      id: "status", name: "StudoX Status", tagline: "Ist alles online?", Mark: StatusMark, external: true, href: "https://status.studox.eu",
      description: "Live-Status aller StudoX-Dienste. Prüfe in Echtzeit, ob alles läuft.",
      accentA: "#4ade80", accentB: "#16a34a", badges: ["Live", "Status"],
    },

    /* ── 🏛️ Fundament — Business & Invest ─────────────────────────────── */
    {
      id: "invest", name: "StudoX Invest", tagline: "Investor Relations", Mark: InvestMark, external: true, href: "https://invest.studox.eu",
      description: "Der Investoren- & Business-Bereich von StudoX — Vision, Zahlen und die Zukunft der Bildung.",
      accentA: "#34d399", accentB: "#fbbf24", badges: ["Business", "Investoren"],
    },

    /* ── ⚠️ Gefahrenzone — Aufklärung & Schutz ───────────────────────── */
    {
      id: "looksmax", name: "Looksmaxing", tagline: "Die Wahrheit über den Schönheitsdruck", Mark: LooksMark, external: true, href: "https://looksmaxing.studox.eu",
      description: "Knallharte Aufklärung über Looksmaxing, die PSL-Skala, Soft- & Hardmaxxing und was die Wissenschaft wirklich sagt. Kein Beschönigen — mit Hilfe-Anlaufstellen.",
      accentA: "#22d3ee", accentB: "#a78bfa", badges: ["Aufklärung", "Schutz", "Hilfe"],
    },
    {
      id: "dossier", name: "StudoX Dossier", tagline: "Menschenrechts-Archiv · beschränkt", Mark: DossierMark, external: true, href: "https://dossier.studox.eu",
      description: "Investigatives Archiv über Kinderleid weltweit. Zugang beschränkt, Inhalte belastend — nur für verifizierte Augen. Wir zeigen die Realität, um sie zu bekämpfen.",
      accentA: "#f43f5e", accentB: "#7f1d1d", badges: ["Investigativ", "18+", "Beschränkt"], restricted: true,
    },
    {
      id: "horror", name: "Die Dunkle Installation", tagline: "Belegte Fakten · Licht & Bass · beschränkt", Mark: HorrorMark, external: true, href: "https://dunkel.studox.eu",
      description: "Eine audiovisuelle Licht-Installation über die dunkelsten belegten Fakten der Welt — jede Zahl mit Quelle. Passwortgeschützt, 16+, mit Inhaltswarnung und Safe-Mode. Kein Effekt. Realität.",
      accentA: "#a855f7", accentB: "#ff0033", badges: ["Installation", "16+", "Zugangswort"], restricted: true,
    },
  ];

  /* Helper: true if the user has platform-wide access (no allowedApps restriction) */
  const isPlatformAdmin = (u) => u?.role === "admin" || u?.role === "solid_ground";

  /* Launch Flow via SSO → generates signed token → redirects with ?sso= */
  const launchFlow = async () => {
    const { token: freshToken, isAuthenticated: authed } = useAuthStore.getState();
    if (!authed || !freshToken) {
      window.location.href = FLOW_URL;
      return;
    }
    try {
      const res = await fetch(`${BACKEND_URL_local}/api/auth/sso-token`, {
        method: "POST",
        headers: { Authorization: `Bearer ${freshToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({}), // no app restriction — flow is the default app
      });
      const data = await res.json();
      if (res.ok && data.token) {
        window.location.href = `${FLOW_URL}?sso=${encodeURIComponent(data.token)}`;
      } else {
        window.location.href = FLOW_URL;
      }
    } catch {
      window.location.href = FLOW_URL;
    }
  };

  /* Launch clubs via SSO → generates nonce → redirects to Clubs */
  const launchClubs = async () => {
    // Always read fresh from store in case we just logged in
    const { token: freshToken, isAuthenticated: authed, user: freshUser } = useAuthStore.getState();
    if (!authed || !freshToken) {
      setPendingLaunch("clubs");
      setShowLogin(true);
      return;
    }
    // Platform admins (admin + solid_ground) always have full access
    if (!isPlatformAdmin(freshUser)) {
      let userApps = ["flow"];
      try { userApps = JSON.parse(freshUser?.allowedApps || '["flow"]'); } catch {}
      if (!userApps.includes("clubs")) {
        setAccessDeniedApp("clubs");
        return;
      }
    }
    try {
      const res = await fetch(`${BACKEND_URL_local}/api/auth/sso-token`, {
        method: "POST",
        headers: { Authorization: `Bearer ${freshToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({ app: "clubs" }),
      });
      const data = await res.json();
      if (res.ok && data.token) {
        window.location.href = `${CLUBS_URL}/auth/callback?sso=${data.token}`;
      } else {
        setAccessDeniedApp("clubs");
      }
    } catch {
      window.open(CLUBS_URL, "_blank");
    }
  };

  /* Launch Create via SSO — same token pattern as Flow */
  const launchCreate = async () => {
    const { token: freshToken, isAuthenticated: authed } = useAuthStore.getState();
    if (!authed || !freshToken) {
      window.location.href = CREATE_URL;
      return;
    }
    try {
      const res = await fetch(`${BACKEND_URL_local}/api/auth/sso-token`, {
        method: "POST",
        headers: { Authorization: `Bearer ${freshToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (res.ok && data.token) {
        window.location.href = `${CREATE_URL}?sso=${encodeURIComponent(data.token)}`;
      } else {
        window.location.href = CREATE_URL;
      }
    } catch {
      window.location.href = CREATE_URL;
    }
  };

  /* Launch Cinema via SSO */
  const launchCinema = async () => {
    const { token: freshToken, isAuthenticated: authed } = useAuthStore.getState();
    if (!authed || !freshToken) {
      window.location.href = CINEMA_URL;
      return;
    }
    try {
      const res = await fetch(`${BACKEND_URL_local}/api/auth/sso-token`, {
        method: "POST",
        headers: { Authorization: `Bearer ${freshToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (res.ok && data.token) {
        window.location.href = `${CINEMA_URL}?sso=${encodeURIComponent(data.token)}`;
      } else {
        window.location.href = CINEMA_URL;
      }
    } catch {
      window.location.href = CINEMA_URL;
    }
  };

  /* Launch Meet via SSO — children's comms, so it is fully SSO-gated */
  const launchMeet = async () => {
    const { token: freshToken, isAuthenticated: authed } = useAuthStore.getState();
    if (!authed || !freshToken) { setPendingLaunch("meet"); setShowLogin(true); return; }
    try {
      const res = await fetch(`${BACKEND_URL_local}/api/auth/sso-token`, {
        method: "POST",
        headers: { Authorization: `Bearer ${freshToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({}), // Meet is base comms infra — no per-app gate; server access is enforced inside Meet
      });
      const data = await res.json();
      window.location.href = res.ok && data.token ? `${MEET_URL}?sso=${encodeURIComponent(data.token)}` : MEET_URL;
    } catch { window.location.href = MEET_URL; }
  };

  /* Launch Adulting via SSO — hart gegated (Kinder-Lernapp), Token direkt mit */
  const ADULTING_URL = "https://adulting.studox.eu";
  const launchAdulting = async () => {
    const { token: freshToken, isAuthenticated: authed } = useAuthStore.getState();
    if (!authed || !freshToken) { setPendingLaunch("adulting"); setShowLogin(true); return; }
    try {
      const res = await fetch(`${BACKEND_URL_local}/api/auth/sso-token`, {
        method: "POST",
        headers: { Authorization: `Bearer ${freshToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      window.location.href = res.ok && data.token ? `${ADULTING_URL}?sso=${encodeURIComponent(data.token)}` : ADULTING_URL;
    } catch { window.location.href = ADULTING_URL; }
  };

  /* Launch Startup via SSO — hart gegated wie Adulting, Token direkt mit */
  const STARTUP_URL = "https://startup.studox.eu";
  const launchStartup = async () => {
    const { token: freshToken, isAuthenticated: authed } = useAuthStore.getState();
    if (!authed || !freshToken) { setPendingLaunch("startup"); setShowLogin(true); return; }
    try {
      const res = await fetch(`${BACKEND_URL_local}/api/auth/sso-token`, {
        method: "POST",
        headers: { Authorization: `Bearer ${freshToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      window.location.href = res.ok && data.token ? `${STARTUP_URL}?sso=${encodeURIComponent(data.token)}` : STARTUP_URL;
    } catch { window.location.href = STARTUP_URL; }
  };

  const handleLaunch = (app) => {
    if (app.comingSoon)  { setShowStepModal(true); return; }
    // Public destinations (info pages, status, support, other-app landings that
    // enforce their own auth) open straight away — no Core gate needed.
    if (app.external) { window.open(app.href, "_blank", "noopener,noreferrer"); return; }
    // Children's content & data — SSO apps are NEVER entered without a session.
    if (!isAuthenticated) { setPendingLaunch(app.id); setShowLogin(true); return; }
    if (app.id === "flow")   { launchFlow();   return; }
    if (app.id === "clubs")  { launchClubs();  return; }
    if (app.id === "create") { launchCreate(); return; }
    if (app.id === "cinema") { launchCinema(); return; }
    if (app.id === "meet")   { launchMeet();   return; }
    if (app.id === "adulting") { launchAdulting(); return; }
    if (app.id === "startup")  { launchStartup();  return; }
  };

  /* After successful login: close modal + execute the queued app launch (if any).
     We call the launch fns directly (they read fresh token from the store) to avoid
     stale-closure issues. */
  /* Clean logout: wipe the shared SSO token + the redirect circuit-breaker so
     the next login starts perfectly fresh, then reload the Core hub. */
  const doLogout = () => {
    try { useAuthStore.getState().logout(); } catch {}
    try { localStorage.removeItem("studox-core-sso-v1"); } catch {}
    try { sessionStorage.removeItem("studox-auth-redirects-v1"); } catch {}
    window.location.href = "/";
  };

  const handleLoginSuccess = () => {
    setShowLogin(false);
    const pending = pendingLaunch;
    setPendingLaunch(null);
    if (!pending) return;
    setTimeout(() => {
      if (pending === "flow") launchFlow();
      else if (pending === "clubs") launchClubs();
      else if (pending === "create") launchCreate();
      else if (pending === "cinema") launchCinema();
      else if (pending === "meet") launchMeet();
      else if (pending === "adulting") launchAdulting();
      else if (pending === "startup") launchStartup();
      else if (pending === "lyrics") window.open(LYRICS_URL, "_blank", "noopener,noreferrer");
    }, 150);
  };

  return (
    <div
      className="fixed inset-0 overflow-hidden flex flex-col"
      style={{ background: "#06021a", color: "#fff" }}
    >
      {/* ── Three.js immersive background ──────────────────────────────── */}
      <ThreeBackground phase={phase} warp={phase === 2} ignitePulse={ignitePulse} />

      {/* ── Film pretitle — the first frame of the continuous shot ────── */}
      {phase === 0 && <PreTitle />}

      {/* ── Boot HUD — letterbox, terminal readout, warp streaks, overlays ── */}
      {phase >= 1 && phase < 4 && <BootHud phase={phase} />}

      {/* ── Ignition flash — a one-shot screen-wide pulse synced to the moment
             the Nexus Gateway 3D background flashes, unifying 2D UI + 3D scene ── */}
      {phase === 3 && (
        <div
          className="fixed inset-0 pointer-events-none"
          style={{
            zIndex: 55,
            background: "radial-gradient(circle at 50% 46%, rgba(255,251,235,0.9) 0%, rgba(251,191,36,0.35) 22%, rgba(139,92,246,0.18) 45%, transparent 72%)",
            animation: "core-ignite-flash 0.9s cubic-bezier(0.16,1,0.3,1) forwards",
          }}
        />
      )}

      {/* ── Trial progress bar — subtle, only while not logged in ─────────── */}
      {!isAuthenticated && !trialLocked && (
        <div style={{
          position: "fixed", top: 0, left: 0, height: 2, zIndex: 60,
          width: trialStarted ? "100%" : "0%",
          transition: "width 25s linear",
          background: "linear-gradient(90deg,#8b5cf6,#06b6d4)",
          opacity: 0.45, pointerEvents: "none",
        }} />
      )}

      {/* ── Grid overlay ───────────────────────────────────────────────── */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: "linear-gradient(rgba(139,92,246,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(139,92,246,0.04) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
          opacity: phase >= 4 ? 1 : 0,
          transition: "opacity 1s ease",
        }}
      />

      {/* ── Premium conic mesh + aurora wash (depth) ───────────────────── */}
      <div className="core-mesh" style={{ opacity: phase >= 4 ? 0.10 : 0, transition: "opacity 1.4s ease" }} />
      <div
        className="absolute inset-0 pointer-events-none core-aurora"
        style={{ opacity: phase >= 4 ? 1 : 0, transition: "opacity 1.4s ease" }}
      />

      {/* ── Radial vignette ────────────────────────────────────────────── */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(ellipse at 50% 50%, transparent 40%, rgba(3,3,16,0.7) 100%)" }}
      />

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* CONTENT                                                         */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <div className="relative z-10 h-full">

        {/* ── Logo assemble phase (centered, before content) ─────────── */}
        {phase >= 3 && phase < 4 && (
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            {/* triple expanding shockrings behind the dropping logo */}
            <div className="absolute inset-0 flex items-center justify-center" style={{ transform: "translateY(-34px)" }}>
              {[0, 1, 2].map((i) => (
                <div key={i} className="absolute rounded-full" style={{
                  width: 150, height: 150,
                  border: `${2.5 - i * 0.8}px solid ${["rgba(255,255,255,0.9)", "rgba(251,191,36,0.7)", "rgba(139,92,246,0.6)"][i]}`,
                  boxShadow: i === 0 ? "0 0 40px rgba(251,191,36,0.5)" : "none",
                  animation: `core-shockring 1.05s cubic-bezier(0.16,1,0.3,1) ${i * 0.13}s both`,
                }} />
              ))}
            </div>

            {/* the logo drops out of warp — blur + overscale collapsing to lock-in */}
            <div style={{ animation: "core-logo-drop 0.8s cubic-bezier(0.16,1,0.3,1) both" }}>
              <div style={{ filter: "drop-shadow(0 0 44px rgba(251,191,36,0.55)) drop-shadow(0 0 70px rgba(139,92,246,0.7))" }}>
                <CoreMark size={128} glow />
              </div>
            </div>

            {/* wordmark assembles from ~1500 real particles, then crossfades
                into the crisp gradient text (handled inside the component) */}
            <div style={{ marginTop: 14 }}>
              <ParticleWordmark />
            </div>

            <div style={{
              marginTop: 10, fontSize: 10, fontWeight: 800, color: "rgba(196,181,253,0.65)",
              fontFamily: "monospace", letterSpacing: 5, opacity: 0,
              animation: "core-boot-subtitle 0.6s ease 0.95s forwards",
            }}>
              ECOSYSTEM GATEWAY
            </div>
          </div>
        )}

        {/* ═══ THE COMMAND BRIDGE — device-adaptive launcher shell ══════════
            Desktop/wide: organ spine + kinetic stage + ⌘K command palette.
            Mobile: phone-style app drawer + bottom category dock + sheets.
            Pure presentation — every launch/auth/trial flow stays HERE in
            StudoXCore and is handed down as props (children's-data safe). */}
        {phase >= 4 && (
          <CoreBridge
            apps={APPS}
            user={user}
            isAuthenticated={isAuthenticated}
            backendUrl={BACKEND_URL_local}
            helpUrl={HELP_URL}
            logo={<CoreMark size={36} glow />}
            onLaunch={handleLaunch}
            onLogin={() => setShowLogin(true)}
            onLogout={doLogout}
            onDashboard={isAuthenticated && (user?.role === "admin" || user?.role === "solid_ground") ? () => setShowDashboard(true) : null}
            onTeacherDash={isAuthenticated && user?.role === "teacher" ? launchFlow : null}
          />
        )}
      </div>

      {/* ── Coming soon modal ────────────────────────────────────────────── */}
      {showStepModal && <ComingSoonModal onClose={() => setShowStepModal(false)} />}

      {/* ── Core login modal ─────────────────────────────────────────────── */}
      {showLogin && !trialLocked && (
        <CoreLoginModal
          initialCode={inviteCode}
          onClose={() => { setShowLogin(false); setPendingLaunch(null); }}
          onSuccess={handleLoginSuccess}
        />
      )}

      {/* ── Trial lock: uncloseable forced login (survives refresh) ───────── */}
      {!isAuthenticated && trialLocked && (
        <CoreLoginModal
          forced
          initialCode={inviteCode}
          onClose={() => {}}
          onSuccess={handleLoginSuccess}
        />
      )}

      {/* ── Access denied modal ──────────────────────────────────────────── */}
      {accessDeniedApp && (
        <AccessDeniedModal
          app={accessDeniedApp}
          onClose={() => setAccessDeniedApp(null)}
        />
      )}

      {/* ── Step notify toast ────────────────────────────────────────────── */}
      {showNotifyToast && (
        <NotifyToast onDone={() => setShowNotifyToast(false)} />
      )}

      {/* ── The merged Admin + Overseer masterpiece dashboard (admin/solid_ground only) ── */}
      {showDashboard && (
        <CoreDashboard onClose={() => setShowDashboard(false)} />
      )}

      {/* ── DSGVO/DSG consent popup — appears once the bridge is visible ── */}
      {phase >= 4 && <CookieConsent />}

      {/* ── NOVA ✦ — the living buddy wanders the bridge (auth only) ────── */}
      {phase >= 4 && isAuthenticated && <NovaBuddy mode="wander" />}
    </div>
  );
}
